import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    App,
    Button,
    Checkbox,
    DatePicker,
    Descriptions,
    Drawer,
    Dropdown,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Pagination,
    Select,
    Space,
    Spin,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Tour,
} from "antd";
import {
    ArrowRightOutlined,
    DownloadOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    ReloadOutlined,
    SwapOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { businessPartnerApi } from "../../../api/contractManagementApi";
import AircraftSelector from "../../../components/common/AircraftSelector";
import salesRevenueApi from "../../../api/salesRevenueApi";
import { useAuth } from "../../../hooks/useAuth";
import { useCurrencies } from "../../../hooks/useCurrencies";
import AdHocAmountPanel from "./AdHocAmountPanel";
import LegFuelBurnPanel from "./LegFuelBurnPanel";
import PerLegBurnBatchPanel from "./PerLegBurnBatchPanel";
import { createSalesRevenueColumns } from "./salesRevenueColumns";
import {
    AIRCRAFT_OPTIONS,
    joinAircraftText,
    parseAircraftText,
    CONTRACT_KIND_OPTIONS,
    CONTRACT_KIND_TEXT,
    CONTRACT_STATUS_OPTIONS,
    CONTRACT_STATUS_TEXT,
    contractStatusEditOptions,
    CREDIT_PAYMENT_METHOD_OPTIONS,
    CREDIT_SOURCE_TYPE_TEXT,
    CUSTOMER_PAYMENT_METHOD_OPTIONS,
    EXTRA_FEE_TYPE_OPTIONS,
    INTERNAL_SECTIONS,
    INVOICE_CREATE_TYPE_OPTIONS,
    INVOICE_STATUS_OPTIONS,
    INVOICE_STATUS_TEXT,
    INVOICE_TYPE_OPTIONS,
    INVOICE_TYPE_TEXT,
    PAYMENT_SOURCE_OPTIONS,
    PRICE_MATCH_TEXT,
    RECEIVABLE_EXCEPTION_OPTIONS,
    RECEIVABLE_EXCEPTION_TEXT,
    RECEIVABLE_STATUS_OPTIONS,
    RECEIVABLE_STATUS_TEXT,
    RECEIVABLE_TYPE_OPTIONS,
    RECEIVABLE_TYPE_TEXT,
    RECEIVABLE_WARNING_TEXT,
    ROUTE_SOURCE_TEXT,
    ROUTE_STATUS_OPTIONS,
    ROUTE_STATUS_TEXT,
    UNBIND_ERROR_TEXT,
    isReceivableRebindable,
} from "./salesRevenueConstants";
import {
    SectionTag,
    downloadCsv,
    formatDate,
    formatDateTime,
    formatUtcDate,
    formatMoney,
    formatRange,
    generateRouteCode,
    getErrorMessage,
    getManualAdjustedConflict,
    getOutstandingAmount,
    getReceivableProgress,
    getToneClass,
    isContractPastEndDate,
    measureFilterControlWidth,
    normalizeAirport,
    normalizeCurrency,
    normalizeStops,
    renderManualAdjustedList,
    serializeDate,
    serializeMonth,
    sortFlightLegs,
} from "./salesRevenueHelpers";
import "./salesRevenueManagement.css";

const CONTRACT_PAGE_SIZE = 30;

const ROUTE_PRICE_KINDS = [
    { value: "UNDIFFERENTIATED", label: "不限飞法" },
    { value: "DIRECT", label: "直飞" },
    { value: "WITH_STOP", label: "经停" },
];

const routePriceKindLabel = (kind) => ROUTE_PRICE_KINDS.find((item) => item.value === kind)?.label || kind;

const routePriceRowsFromRecord = (record) => {
    const rows = [];
    const push = (kind, amount, status) => {
        if (status === "ACTIVE" && amount != null && amount !== "") {
            rows.push({ kind, unit_price: Number(amount) });
        }
    };
    push("UNDIFFERENTIATED", record?.undifferentiated_unit_price, record?.undifferentiated_status);
    push("DIRECT", record?.direct_unit_price, record?.direct_status);
    push("WITH_STOP", record?.with_stop_unit_price, record?.with_stop_status);
    return rows;
};

const SalesRevenueManagementPage = ({ initialSection = "routes" }) => {
    const { message: messageApi, modal: modalApi } = App.useApp();
    const { hasPermission } = useAuth();
    const { currencyOptions } = useCurrencies();

    const canWriteContract = hasPermission("sales:revenue:contract:write");
    const canWriteReceivable = hasPermission("sales:revenue:receivable:write");
    const canWriteInvoice = hasPermission("sales:revenue:invoice:write");
    const canWritePayment = hasPermission("sales:revenue:payment:write");
    const canGenerateFuel = hasPermission("sales:revenue:fuel-generate");

    const activeSection = INTERNAL_SECTIONS.some((item) => item.key === initialSection) ? initialSection : "routes";
    const navigate = useNavigate();
    const { contractId: routeContractId } = useParams();

    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);

    const [routeFilters, setRouteFilters] = useState({
        originAirport: "",
        destinationAirport: "",
        keyword: "",
        status: "ACTIVE",
    });
    const [routePagination, setRoutePagination] = useState({ page: 1, pageSize: 20 });
    const [routeList, setRouteList] = useState({ items: [], total: 0, page: 1, page_size: 20 });
    const [routeLoading, setRouteLoading] = useState(false);
    const [routeModalOpen, setRouteModalOpen] = useState(false);
    const [routeSubmitting, setRouteSubmitting] = useState(false);
    const [editingRoute, setEditingRoute] = useState(null);
    const [contractKeywordDraft, setContractKeywordDraft] = useState("");
    const [contractFilters, setContractFilters] = useState({
        keyword: "",
        customerId: "",
        status: "",
    });
    const [keepContractList, setKeepContractList] = useState(!routeContractId);
    const [contractPage, setContractPage] = useState(1);
    const [contractList, setContractList] = useState({ items: [], total: 0 });
    const [contractLoading, setContractLoading] = useState(false);
    const [contractLoadingMore, setContractLoadingMore] = useState(false);
    const contractGenerationRef = useRef(0);
    const contractLoadingRef = useRef(false);
    const contractLoadingMoreRef = useRef(false);
    const contractHasMore = contractList.items.length < contractList.total;
    const [contractModalOpen, setContractModalOpen] = useState(false);
    const [contractSubmitting, setContractSubmitting] = useState(false);
    const [contractHeaderEditing, setContractHeaderEditing] = useState(false);
    const [editingContract, setEditingContract] = useState(null);

    const [receivableViewKey, setReceivableViewKey] = useState("flight");
    const [receivableFilters, setReceivableFilters] = useState({
        customerId: "",
        contractId: "",
        route: "",
        status: "",
        type: "",
        bookingNo: "",
        flightMonth: null,
        hasException: "",
    });
    const [receivablePagination, setReceivablePagination] = useState({ page: 1, pageSize: 20 });
    const [receivableList, setReceivableList] = useState({ items: [], total: 0, page: 1, page_size: 20 });
    const [receivableLoading, setReceivableLoading] = useState(false);
    const [receivableDetailOpen, setReceivableDetailOpen] = useState(false);
    const [receivableDetailLoading, setReceivableDetailLoading] = useState(false);
    const [receivableDetail, setReceivableDetail] = useState(null);
    const [bookingDetailOpen, setBookingDetailOpen] = useState(false);
    const [bookingDetailKey, setBookingDetailKey] = useState(null);
    const [bookingFlights, setBookingFlights] = useState([]);
    const [bookingFlightsLoading, setBookingFlightsLoading] = useState(false);
    const [receivableContractOptions, setReceivableContractOptions] = useState([]);
    const [receivableContractOptionsLoading, setReceivableContractOptionsLoading] = useState(false);
    const [batchGenerateModalOpen, setBatchGenerateModalOpen] = useState(false);
    const [batchGenerateSubmitting, setBatchGenerateSubmitting] = useState(false);
    const [batchGenerateResult, setBatchGenerateResult] = useState(null);
    const [fuelCalcModalOpen, setFuelCalcModalOpen] = useState(false);
    const [fuelCalcPreviewLoading, setFuelCalcPreviewLoading] = useState(false);
    const [fuelCalcGenerating, setFuelCalcGenerating] = useState(false);
    const [fuelCalcResult, setFuelCalcResult] = useState(null);
    const [extraFeeModalOpen, setExtraFeeModalOpen] = useState(false);
    const [extraFeeSubmitting, setExtraFeeSubmitting] = useState(false);
    const [extraFeeContext, setExtraFeeContext] = useState(null);
    const [invoiceFilters, setInvoiceFilters] = useState({
        customerId: "",
        contractId: "",
        invoiceNo: "",
        invoiceType: "",
        status: "",
        bookingNo: "",
        flightMonth: null,
    });
    const [invoicePagination, setInvoicePagination] = useState({ page: 1, pageSize: 20 });
    const [invoiceList, setInvoiceList] = useState({ items: [], total: 0, page: 1, page_size: 20 });
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
    const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);
    const [invoiceDetail, setInvoiceDetail] = useState(null);
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
    const [invoiceCandidateType, setInvoiceCandidateType] = useState(null);
    const [selectedInvoiceReceivableIds, setSelectedInvoiceReceivableIds] = useState([]);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [paymentSubmitting, setPaymentSubmitting] = useState(false);
    const [paymentInvoice, setPaymentInvoice] = useState(null);
    const [customerCredits, setCustomerCredits] = useState([]);
    const [customerCreditsLoading, setCustomerCreditsLoading] = useState(false);

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [contractDetailError, setContractDetailError] = useState(null);
    const [detailTabKey, setDetailTabKey] = useState("prices");
    const [detailContract, setDetailContract] = useState(null);
    const [contractBookings, setContractBookings] = useState([]);
    const [contractBookingsLoading, setContractBookingsLoading] = useState(false);
    const [contractBookingsLoaded, setContractBookingsLoaded] = useState(false);
    const [contractBookingsQuery, setContractBookingsQuery] = useState({ page: 1, pageSize: 20, total: 0 });

    const [routePriceModalOpen, setRoutePriceModalOpen] = useState(false);
    const [routePriceSubmitting, setRoutePriceSubmitting] = useState(false);
    const [editingRoutePrice, setEditingRoutePrice] = useState(null);
    const [routePoolOptions, setRoutePoolOptions] = useState([]);
    const [routePoolOptionsLoading, setRoutePoolOptionsLoading] = useState(false);

    const [fuelTierModalOpen, setFuelTierModalOpen] = useState(false);
    const [fuelTierSubmitting, setFuelTierSubmitting] = useState(false);
    const [editingFuelTier, setEditingFuelTier] = useState(null);
    const [fuelTierBatchOpen, setFuelTierBatchOpen] = useState(false);
    const [fuelTierBatchSubmitting, setFuelTierBatchSubmitting] = useState(false);
    const [fuelTierRows, setFuelTierRows] = useState([]);
    const [ladderForm] = Form.useForm();
    const fuelTierRowSeq = useRef(0);

    const [prepaymentModalOpen, setPrepaymentModalOpen] = useState(false);
    const [prepaymentSubmitting, setPrepaymentSubmitting] = useState(false);

    const [rebindModalOpen, setRebindModalOpen] = useState(false);
    const [rebindTarget, setRebindTarget] = useState(null);
    const [rebindContractOptions, setRebindContractOptions] = useState([]);
    const [rebindContractOptionsLoading, setRebindContractOptionsLoading] = useState(false);
    const [rebindSubmitting, setRebindSubmitting] = useState(false);

    const [adjustAmountModalOpen, setAdjustAmountModalOpen] = useState(false);
    const [adjustAmountSubmitting, setAdjustAmountSubmitting] = useState(false);
    const [adjustAmountTarget, setAdjustAmountTarget] = useState(null);

    // AD_HOC 一班一议：从合同详情「绑定订单」打开燃油手填弹窗
    const [adHocPanelBooking, setAdHocPanelBooking] = useState(null);

    const [contractTourOpen, setContractTourOpen] = useState(false);
    const [receivableTourOpen, setReceivableTourOpen] = useState(false);
    const [contractModalGuideOpen, setContractModalGuideOpen] = useState(false);
    const [contractModalAutoGuide, setContractModalAutoGuide] = useState(false);
    const contractFilterRef = useRef(null);
    const contractTableRef = useRef(null);
    const contractTableScrollRef = useRef(0);
    const detailRequestRef = useRef(0);
    const contractCreateBtnRef = useRef(null);
    const contractFieldNoRef = useRef(null);
    const contractFieldKindRef = useRef(null);
    const contractFieldDateRef = useRef(null);
    const contractFieldFuelRef = useRef(null);
    const receivableActionsRef = useRef(null);
    const receivableFilterRef = useRef(null);
    const receivableGuideRef = useRef(null);
    const receivableViewTabsRef = useRef(null);

    const [routeForm] = Form.useForm();
    const [contractForm] = Form.useForm();
    // 合同表单实时类型值：AD_HOC 时隐藏基准油价、有效期改为可空
    const contractKindDraft = Form.useWatch("contract_kind", contractForm);
    const [batchGenerateForm] = Form.useForm();
    const [fuelCalcForm] = Form.useForm();
    const [extraFeeForm] = Form.useForm();
    const [routePriceForm] = Form.useForm();
    const [fuelTierForm] = Form.useForm();
    const [prepaymentForm] = Form.useForm();
    const [bookingRebindForm] = Form.useForm();
    const [invoiceForm] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [adjustAmountForm] = Form.useForm();

    const watchedRouteOrigin = Form.useWatch("origin_airport", routeForm);
    const watchedRouteDestination = Form.useWatch("destination_airport", routeForm);
    const watchedBatchGenerateDryRun = Form.useWatch("dryRun", batchGenerateForm);
    const watchedPaymentSourceType = Form.useWatch("source_type", paymentForm);
    const routeCodePreview = generateRouteCode(watchedRouteOrigin, watchedRouteDestination);

    const customerOptions = useMemo(
        () =>
            customers.map((item) => ({
                label: item.name,
                value: item.id,
            })),
        [customers],
    );
    const contractCustomerFilterWidth = useMemo(
        () => measureFilterControlWidth(["全部客户", ...customers.map((item) => item.name)], { withArrow: true, withClear: true }),
        [customers],
    );
    const contractStatusFilterWidth = useMemo(
        () => measureFilterControlWidth(CONTRACT_STATUS_OPTIONS.map((item) => item.label), { withArrow: true }),
        [],
    );
    const contractKeywordFilterWidth = useMemo(
        () => measureFilterControlWidth(["合同编号、客户名称或机型，回车搜索"], { withArrow: false }),
        [],
    );

    // 改绑下拉：标注合同类型与有效期（AD_HOC 允许无有效期 = 永久有效）
    const buildContractOptionLabel = (item) => {
        const kindText = CONTRACT_KIND_TEXT[item.contract_kind] || "阶梯合同";
        const rangeText = !item.start_date && !item.end_date ? "永久有效" : `${item.start_date || "?"} ~ ${item.end_date || "?"}`;
        return `${item.contract_no}（${kindText} · ${rangeText}）`;
    };

    const rebindContractSelectOptions = useMemo(
        () =>
            rebindContractOptions.map((item) => ({
                label: buildContractOptionLabel(item),
                value: item.id,
            })),
        [rebindContractOptions],
    );

    const routeAirportOptions = useMemo(() => {
        const codes = new Set();
        routeList.items.forEach((item) => {
            if (item.origin_airport) {
                codes.add(item.origin_airport);
            }
            if (item.destination_airport) {
                codes.add(item.destination_airport);
            }
        });
        return Array.from(codes)
            .sort()
            .map((item) => ({ label: item, value: item }));
    }, [routeList.items]);

    const routeStats = useMemo(() => {
        const total = routeList.total || 0;
        const active = routeList.items.filter((item) => item.status === "ACTIVE").length;
        const inactive = routeList.items.filter((item) => item.status === "INACTIVE").length;
        return { total, active, inactive };
    }, [routeList]);

    // 列表已按订单聚合，这里把各订单下的应收明细拍平，供开票指引/批量开票候选/导出等按明细取数
    const receivableDetailItems = useMemo(
        () =>
            (receivableList.items || []).flatMap((group) =>
                (group.receivables || []).map((item) => ({
                    ...item,
                    booking_no: group.booking_no,
                    charter_booking_id: group.booking_id,
                    customer_id: group.customer_id,
                    customer_name: group.customer_name,
                    contract_id: group.contract_id,
                    contract_no: group.contract_no,
                    route_display_snapshot: group.route_display,
                    aircraft_type_snapshot: group.aircraft_type,
                    flight_date_snapshot: group.flight_date,
                    flight_legs_snapshot: group.flight_legs_snapshot,
                })),
            ),
        [receivableList.items],
    );

    const receivableRouteOptions = useMemo(() => {
        const values = new Set();
        (receivableList.items || []).forEach((group) => {
            if (group.route_display) {
                values.add(group.route_display);
            }
        });
        return Array.from(values)
            .sort()
            .map((item) => ({ label: item, value: item }));
    }, [receivableList.items]);

    const receivableContractSelectOptions = useMemo(
        () =>
            receivableContractOptions.map((item) => ({
                label: `${item.contract_no}${item.customer_name ? ` / ${item.customer_name}` : ""}`,
                value: item.id,
            })),
        [receivableContractOptions],
    );

    // 「燃油调整计算 → 阶梯合同」页签专用：只列阶梯合同，按段油耗合同走旁边的「按段油耗」页签
    const tierContractSelectOptions = useMemo(
        () =>
            receivableContractOptions
                .filter((item) => (item.contract_kind || "TIER") === "TIER")
                .map((item) => ({
                    label: `${item.contract_no}${item.customer_name ? ` / ${item.customer_name}` : ""}`,
                    value: item.id,
                })),
        [receivableContractOptions],
    );

    const extraFeeBookingOptions = useMemo(() => {
        return (receivableList.items || [])
            .filter((group) => group.booking_id && group.booking_no)
            .map((group) => ({
                label: `${group.booking_no} / ${group.route_display || "未识别航线"}`,
                value: group.booking_id,
                item: {
                    booking_no: group.booking_no,
                    contract_id: group.contract_id,
                    currency: group.summary?.currency || "USD",
                    flight_date_snapshot: group.flight_date,
                    billing_timezone: "UTC",
                },
            }));
    }, [receivableList.items]);

    const receivableGuideStats = useMemo(() => {
        const items = receivableDetailItems;
        const sumAmount = (predicate) =>
            items.reduce((total, item) => (predicate(item) ? total + Number(item.amount || 0) : total), 0);

        return {
            pendingBaseAmount: sumAmount((item) => item.type === "BASE_FARE" && item.status === "PENDING_INVOICE"),
            pendingFuelAmount: sumAmount((item) => item.type === "FUEL_SURCHARGE" && item.status === "PENDING_INVOICE"),
            pendingExtraAmount: sumAmount((item) => item.type === "EXTRA_FEE" && item.status === "PENDING_INVOICE"),
            totalPendingAmount: sumAmount((item) => item.status === "PENDING_INVOICE"),
            exceptionCount: items.filter((item) => item.exception_code).length,
        };
    }, [receivableDetailItems]);

    const invoiceCandidates = useMemo(() => {
        const typeMap = {
            BASE_WEEKLY: ["BASE_FARE"],
            FUEL_MONTHLY: ["FUEL_SURCHARGE"],
            EXTRA_FEE: ["EXTRA_FEE"],
            MIXED: ["BASE_FARE", "FUEL_SURCHARGE", "EXTRA_FEE"],
        };
        const allowedTypes = typeMap[invoiceCandidateType] || ["BASE_FARE", "FUEL_SURCHARGE", "EXTRA_FEE"];
        return receivableDetailItems.filter(
            (item) => item.status === "PENDING_INVOICE" && allowedTypes.includes(item.type),
        );
    }, [invoiceCandidateType, receivableDetailItems]);

    const invoiceCandidateTotalAmount = useMemo(
        () =>
            invoiceCandidates.reduce(
                (total, item) => (selectedInvoiceReceivableIds.includes(item.id) ? total + Number(item.amount || 0) : total),
                0,
            ),
        [invoiceCandidates, selectedInvoiceReceivableIds],
    );

    const invoiceSummary = useMemo(() => {
        const items = invoiceList.items || [];
        const sumAmount = (field, predicate = () => true) =>
            items.reduce((total, item) => (predicate(item) ? total + Number(item[field] || 0) : total), 0);
        return {
            issuedAmount: sumAmount("total_amount", (item) => item.status === "ISSUED"),
            partialAmount: sumAmount("total_amount", (item) => item.status === "PARTIALLY_PAID"),
            paidAmount: sumAmount("paid_amount"),
            outstandingAmount: items.reduce((total, item) => total + getOutstandingAmount(item.total_amount, item.paid_amount), 0),
        };
    }, [invoiceList.items]);

    const creditOptions = useMemo(
        () =>
            customerCredits
                .filter((item) => !paymentInvoice?.currency || item.currency === paymentInvoice.currency)
                .map((item) => ({
                    label: `${CREDIT_SOURCE_TYPE_TEXT[item.source_type] || item.source_type} · 剩余 ${formatMoney(item.remaining_amount)} ${item.currency}`,
                    value: item.id,
                })),
        [customerCredits, paymentInvoice?.currency],
    );

    const loadCustomers = useCallback(async () => {
        setCustomersLoading(true);
        try {
            const response = await businessPartnerApi.getCustomers();
            const list = Array.isArray(response.data)
                ? response.data
                : response.data?.items || [];
            setCustomers(list);
        } catch (error) {
            messageApi.error(getErrorMessage(error, "获取客户列表失败"));
        } finally {
            setCustomersLoading(false);
        }
    }, [messageApi]);

    const loadRoutes = useCallback(async () => {
        setRouteLoading(true);
        try {
            const response = await salesRevenueApi.listRoutes({
                page: routePagination.page,
                pageSize: routePagination.pageSize,
                originAirport: routeFilters.originAirport || undefined,
                destinationAirport: routeFilters.destinationAirport || undefined,
                keyword: routeFilters.keyword || undefined,
                status: routeFilters.status || undefined,
            });
            setRouteList(response.data);
        } catch (error) {
            messageApi.error(getErrorMessage(error, "获取航线列表失败"));
        } finally {
            setRouteLoading(false);
        }
    }, [messageApi, routeFilters, routePagination.page, routePagination.pageSize]);

    const loadRoutePoolOptions = useCallback(async (keyword) => {
        setRoutePoolOptionsLoading(true);
        try {
            const response = await salesRevenueApi.listRoutes({
                page: 1,
                pageSize: 200,
                status: "ACTIVE",
                keyword: keyword?.trim() || undefined,
            });
            const items = Array.isArray(response.data) ? response.data : response.data?.items || [];
            setRoutePoolOptions(
                items.map((item) => ({
                    value: item.id,
                    label: `${item.origin_airport} → ${item.destination_airport}`,
                    origin_airport: item.origin_airport,
                    destination_airport: item.destination_airport,
                })),
            );
        } catch (error) {
            messageApi.error(getErrorMessage(error, "获取航线池失败"));
        } finally {
            setRoutePoolOptionsLoading(false);
        }
    }, [messageApi]);

    const routePoolSearchTimer = useRef(null);
    const handleRoutePoolSearch = (value) => {
        if (routePoolSearchTimer.current) {
            window.clearTimeout(routePoolSearchTimer.current);
        }
        routePoolSearchTimer.current = window.setTimeout(() => {
            loadRoutePoolOptions(value);
        }, 300);
    };

    const loadContracts = useCallback(async ({ page = 1, reset = true } = {}) => {
        let generation;
        if (reset) {
            generation = contractGenerationRef.current + 1;
            contractGenerationRef.current = generation;
            contractLoadingMoreRef.current = false;
            contractLoadingRef.current = true;
            setContractLoading(true);
            setContractLoadingMore(false);
        } else {
            if (contractLoadingMoreRef.current || contractLoadingRef.current) return;
            generation = contractGenerationRef.current;
            if (generation === 0) return;
            contractLoadingMoreRef.current = true;
            setContractLoadingMore(true);
        }
        try {
            const response = await salesRevenueApi.listContracts({
                page,
                pageSize: CONTRACT_PAGE_SIZE,
                keyword: contractFilters.keyword || undefined,
                customerId: contractFilters.customerId || undefined,
                status: contractFilters.status || undefined,
            });
            if (generation !== contractGenerationRef.current) return;
            const data = response.data || {};
            const items = data.items || [];
            const nextTotal = data.total || 0;
            setContractList((prev) => ({
                items: reset ? items : [...prev.items, ...items],
                total: nextTotal,
            }));
            setContractPage(page);
        } catch (error) {
            if (generation !== contractGenerationRef.current) return;
            messageApi.error(getErrorMessage(error, "获取合同列表失败"));
        } finally {
            if (generation === contractGenerationRef.current) {
                contractLoadingRef.current = false;
                setContractLoading(false);
                setContractLoadingMore(false);
                contractLoadingMoreRef.current = false;
            }
        }
    }, [contractFilters, messageApi]);

    const loadMoreContracts = () => {
        if (!contractHasMore || contractLoadingRef.current || contractLoadingMoreRef.current) return;
        loadContracts({ page: contractPage + 1, reset: false });
    };

    const rememberContractListScroll = () => {
        const body = contractTableRef.current?.querySelector(".ant-table-body");
        if (body) contractTableScrollRef.current = body.scrollTop;
    };

    const handleContractTableScroll = (event) => {
        const target = event?.currentTarget || event?.target;
        if (!target || typeof target.scrollTop !== "number") return;
        contractTableScrollRef.current = target.scrollTop;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceToBottom < 100) {
            loadMoreContracts();
        }
    };

    const loadReceivableContracts = useCallback(async () => {
        setReceivableContractOptionsLoading(true);
        try {
            const response = await salesRevenueApi.listContracts({
                page: 1,
                pageSize: 200,
            });
            setReceivableContractOptions(response.data?.items || []);
        } catch (error) {
            messageApi.error(getErrorMessage(error, "获取合同选项失败"));
        } finally {
            setReceivableContractOptionsLoading(false);
        }
    }, [messageApi]);

    const loadReceivables = useCallback(async () => {
        setReceivableLoading(true);
        try {
            const response = await salesRevenueApi.listReceivablesByBooking({
                page: receivablePagination.page,
                pageSize: receivablePagination.pageSize,
                customerId: receivableFilters.customerId || undefined,
                contractId: receivableFilters.contractId || undefined,
                route: receivableFilters.route || undefined,
                status: receivableFilters.status || undefined,
                type: receivableFilters.type || undefined,
                bookingNo: receivableFilters.bookingNo || undefined,
                flightMonth: serializeMonth(receivableFilters.flightMonth),
                hasException:
                    receivableFilters.hasException === "YES"
                        ? true
                        : receivableFilters.hasException === "NO"
                          ? false
                          : undefined,
            });
            setReceivableList(response.data);
        } catch (error) {
            messageApi.error(getErrorMessage(error, "获取应收列表失败"));
        } finally {
            setReceivableLoading(false);
        }
    }, [messageApi, receivableFilters, receivablePagination.page, receivablePagination.pageSize]);

    const loadInvoices = useCallback(async () => {
        setInvoiceLoading(true);
        try {
            const response = await salesRevenueApi.listInvoices({
                page: invoicePagination.page,
                pageSize: invoicePagination.pageSize,
                customerId: invoiceFilters.customerId || undefined,
                contractId: invoiceFilters.contractId || undefined,
                invoiceNo: invoiceFilters.invoiceNo || undefined,
                invoiceType: invoiceFilters.invoiceType || undefined,
                status: invoiceFilters.status || undefined,
                bookingNo: invoiceFilters.bookingNo || undefined,
                flightMonth: serializeMonth(invoiceFilters.flightMonth),
            });
            setInvoiceList(response.data);
        } catch (error) {
            messageApi.error(getErrorMessage(error, "获取发票列表失败"));
        } finally {
            setInvoiceLoading(false);
        }
    }, [invoiceFilters, invoicePagination.page, invoicePagination.pageSize, messageApi]);

    const loadContractDetail = useCallback(
        async (contractId, options = {}) => {
            const { keepOpen = true } = options;
            detailRequestRef.current += 1;
            const requestId = detailRequestRef.current;
            setDetailLoading(true);
            setContractDetailError(null);
            if (keepOpen) {
                setDetailOpen(true);
            }
            try {
                const response = await salesRevenueApi.getContract(contractId);
                if (requestId !== detailRequestRef.current) return;
                setDetailContract(response.data);
            } catch (error) {
                if (requestId !== detailRequestRef.current) return;
                const errorText = getErrorMessage(error, "获取合同详情失败");
                messageApi.error(errorText);
                setContractDetailError({ contractId, message: errorText });
                if (keepOpen) {
                    setDetailOpen(false);
                }
            } finally {
                if (requestId === detailRequestRef.current) {
                    setDetailLoading(false);
                }
            }
        },
        [messageApi],
    );

    const loadContractBookings = useCallback(
        async (contractId, page = 1, pageSize = 20) => {
            if (!contractId) {
                return;
            }
            setContractBookingsLoading(true);
            try {
                const response = await salesRevenueApi.listContractBookings(contractId, { page, pageSize });
                setContractBookings(response.data?.items || []);
                setContractBookingsQuery({
                    page: response.data?.page || page,
                    pageSize: response.data?.page_size || pageSize,
                    total: response.data?.total || 0,
                });
                setContractBookingsLoaded(true);
            } catch (error) {
                messageApi.error(getErrorMessage(error, "获取合同绑定订单失败"));
            } finally {
                setContractBookingsLoading(false);
            }
        },
        [messageApi],
    );

    const loadReceivableDetail = useCallback(
        async (receivableId, options = {}) => {
            const { keepOpen = true } = options;
            setReceivableDetailLoading(true);
            if (keepOpen) {
                setReceivableDetailOpen(true);
            }
            try {
                const response = await salesRevenueApi.getReceivable(receivableId);
                setReceivableDetail(response.data);
            } catch (error) {
                messageApi.error(getErrorMessage(error, "获取应收详情失败"));
                if (keepOpen) {
                    setReceivableDetailOpen(false);
                }
            } finally {
                setReceivableDetailLoading(false);
            }
        },
        [messageApi],
    );

    const loadInvoiceDetail = useCallback(
        async (invoiceId, options = {}) => {
            const { keepOpen = true } = options;
            setInvoiceDetailLoading(true);
            if (keepOpen) {
                setInvoiceDetailOpen(true);
            }
            try {
                const response = await salesRevenueApi.getInvoice(invoiceId);
                setInvoiceDetail(response.data);
            } catch (error) {
                messageApi.error(getErrorMessage(error, "获取发票详情失败"));
                if (keepOpen) {
                    setInvoiceDetailOpen(false);
                }
            } finally {
                setInvoiceDetailLoading(false);
            }
        },
        [messageApi],
    );

    const loadCustomerCredits = useCallback(
        async (customerId) => {
            if (!customerId) {
                setCustomerCredits([]);
                return;
            }
            setCustomerCreditsLoading(true);
            try {
                const response = await salesRevenueApi.listCustomerCredits(customerId, {
                    page: 1,
                    pageSize: 100,
                    status: "ACTIVE",
                });
                setCustomerCredits(response.data?.items || []);
            } catch (error) {
                messageApi.error(getErrorMessage(error, "获取客户可用余额失败"));
            } finally {
                setCustomerCreditsLoading(false);
            }
        },
        [messageApi],
    );

    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    useEffect(() => {
        if (activeSection === "routes") {
            loadRoutes();
        }
    }, [activeSection, loadRoutes]);

    useEffect(() => {
        if (activeSection === "contracts") {
            loadContracts();
        }
    }, [activeSection, loadContracts]);

    useEffect(() => {
        if (activeSection === "receivables") {
            loadReceivables();
            loadReceivableContracts();
            loadInvoices();
        }
    }, [activeSection, loadInvoices, loadReceivableContracts, loadReceivables]);

    useEffect(() => {
        if (!paymentModalOpen) {
            return;
        }
        if (watchedPaymentSourceType === "CREDIT_LEDGER") {
            const currentMethod = paymentForm.getFieldValue("payment_method");
            if (!CREDIT_PAYMENT_METHOD_OPTIONS.some((item) => item.value === currentMethod)) {
                paymentForm.setFieldValue("payment_method", CREDIT_PAYMENT_METHOD_OPTIONS[0].value);
            }
            const currentUsages = paymentForm.getFieldValue("credit_usages");
            if ((!currentUsages || !currentUsages.length) && customerCredits.length === 1) {
                paymentForm.setFieldValue("credit_usages", [
                    {
                        credit_ledger_id: customerCredits[0].id,
                        amount: Math.min(
                            getOutstandingAmount(paymentInvoice?.total_amount, paymentInvoice?.paid_amount),
                            Number(customerCredits[0].remaining_amount || 0),
                        ),
                    },
                ]);
            }
            return;
        }
        const currentMethod = paymentForm.getFieldValue("payment_method");
        if (!CUSTOMER_PAYMENT_METHOD_OPTIONS.some((item) => item.value === currentMethod)) {
            paymentForm.setFieldValue("payment_method", CUSTOMER_PAYMENT_METHOD_OPTIONS[0].value);
        }
        if ((paymentForm.getFieldValue("credit_usages") || []).length) {
            paymentForm.setFieldValue("credit_usages", []);
        }
    }, [customerCredits, paymentForm, paymentInvoice, paymentModalOpen, watchedPaymentSourceType]);

    const openCreateRouteModal = () => {
        setEditingRoute(null);
        routeForm.resetFields();
        routeForm.setFieldsValue({
            status: "ACTIVE",
            source_type: "MANUAL",
            historical_stops: [],
        });
        setRouteModalOpen(true);
    };

    const openEditRouteModal = (record) => {
        setEditingRoute(record);
        routeForm.setFieldsValue({
            route_code: record.route_code,
            origin_airport: record.origin_airport,
            destination_airport: record.destination_airport,
            name: record.name,
            status: record.status,
            source_type: record.source_type,
            historical_stops: record.historical_stops || [],
        });
        setRouteModalOpen(true);
    };

    const handleRouteSubmit = async () => {
        try {
            const values = await routeForm.validateFields();
            setRouteSubmitting(true);
            if (editingRoute) {
                await salesRevenueApi.updateRoute(editingRoute.id, {
                    name: values.name?.trim() || null,
                    status: values.status,
                    historical_stops: normalizeStops(values.historical_stops),
                });
                messageApi.success("航线更新成功");
            } else {
                await salesRevenueApi.createRoute({
                    origin_airport: normalizeAirport(values.origin_airport),
                    destination_airport: normalizeAirport(values.destination_airport),
                    name: values.name?.trim() || null,
                    status: values.status,
                    source_type: "MANUAL",
                    historical_stops: normalizeStops(values.historical_stops),
                });
                messageApi.success("航线创建成功");
            }
            setRouteModalOpen(false);
            await loadRoutes();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, editingRoute ? "更新航线失败" : "创建航线失败"));
        } finally {
            setRouteSubmitting(false);
        }
    };

    const handleRouteStatusChange = (record) => {
        const nextAction = record.status === "ACTIVE" ? "停用" : "启用";
        modalApi.confirm({
            title: `确认${nextAction}航线`,
            icon: <ExclamationCircleOutlined />,
            content: `确定要${nextAction}航线 ${record.route_code} 吗？`,
            okText: nextAction,
            cancelText: "取消",
            okButtonProps: {
                danger: record.status === "ACTIVE",
            },
            onOk: async () => {
                try {
                    if (record.status === "ACTIVE") {
                        await salesRevenueApi.deactivateRoute(record.id);
                    } else {
                        await salesRevenueApi.activateRoute(record.id);
                    }
                    messageApi.success(`航线已${nextAction}`);
                    await loadRoutes();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, `${nextAction}航线失败`));
                }
            },
        });
    };

    const openCreateContractModal = () => {
        setEditingContract(null);
        contractForm.resetFields();
        contractForm.setFieldsValue({
            currency: "USD",
            status: "DRAFT",
            contract_kind: "TIER",
        });
        setContractModalOpen(true);
    };

    const handleContractSubmit = async () => {
        try {
            const values = await contractForm.validateFields();
            const kind = editingContract?.contract_kind || values.contract_kind || "TIER";
            const isAcmi = kind === "ACMI";
            const baseFuelHidden = kind === "AD_HOC" || kind === "PER_LEG_BURN" || isAcmi;
            const keepExistingBaseFuel = baseFuelHidden && Boolean(editingContract) && !isAcmi;
            const baseFuelPrice = baseFuelHidden ? null : values.base_fuel_price_per_mt ?? null;
            const payload = {
                contract_no: values.contract_no.trim(),
                start_date: serializeDate(values.start_date),
                end_date: serializeDate(values.end_date),
                currency: normalizeCurrency(values.currency),
                ...(keepExistingBaseFuel ? {} : { base_fuel_price_per_mt: baseFuelPrice }),
                ...(isAcmi
                    ? {
                          acmi_registration: values.acmi_registration?.trim(),
                          acmi_lump_sum: values.acmi_lump_sum,
                      }
                    : {}),
                frequency_text: isAcmi ? null : values.frequency_text?.trim() || null,
                aircraft_text: isAcmi ? null : joinAircraftText(values.aircraft_text),
                status: values.status,
                remark: values.remark?.trim() || null,
            };
            const savedId = editingContract?.id || null;

            setContractSubmitting(true);
            if (editingContract) {
                await salesRevenueApi.updateContract(editingContract.id, payload);
                messageApi.success("合同更新成功");
            } else {
                const createStatus = isAcmi && values.status === "ACTIVE" ? "ACTIVE" : "DRAFT";
                await salesRevenueApi.createContract({
                    ...payload,
                    status: createStatus,
                    customer_id: values.customer_id,
                    contract_kind: values.contract_kind || "TIER",
                });
                messageApi.success(createStatus === "ACTIVE" ? "合同已创建并生效" : "合同创建成功");
            }
            setContractModalOpen(false);
            await loadContracts();
            if (savedId && routeContractId === savedId) {
                contractFormFilledRef.current = null;
                setContractHeaderEditing(false);
                await loadContractDetail(savedId, { keepOpen: false });
            } else if (savedId && detailOpen && detailContract?.id === savedId) {
                await loadContractDetail(savedId, { keepOpen: false });
            }
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, editingContract ? "更新合同失败" : "创建合同失败"));
        } finally {
            setContractSubmitting(false);
        }
    };

    const handleDeleteAcmiSubstitute = async (substituteId) => {
        if (!detailContract) return;
        try {
            await salesRevenueApi.deleteAcmiSubstitute(detailContract.id, substituteId);
            messageApi.success("已删除这一天的顶班");
            await loadContractDetail(detailContract.id);
        } catch (error) {
            messageApi.error(getErrorMessage(error, "删除顶班失败"));
        }
    };

    const handleContractStatusChange = (record) => {
        const isActive = record.status === "ACTIVE";
        const nextAction = isActive ? "终止" : record.status === "EXPIRED" ? "重新启用" : "启用";
        modalApi.confirm({
            title: `确认${nextAction}合同`,
            icon: <ExclamationCircleOutlined />,
            content: isActive
                ? `终止后 ${record.contract_no} 不再参与计费，合同编号可以留给新合同。确定终止吗？`
                : `启用前会确认已有航线价格。同一客户的多份合同可以落在同一时段。确定${nextAction} ${record.contract_no} 吗？`,
            okText: nextAction,
            cancelText: "取消",
            okButtonProps: { danger: isActive },
            onOk: async () => {
                try {
                    if (isActive) {
                        await salesRevenueApi.terminateContract(record.id);
                    } else {
                        await salesRevenueApi.activateContract(record.id);
                    }
                    messageApi.success(`合同已${nextAction}`);
                    await Promise.all([
                        loadContracts(),
                        detailContract?.id === record.id ? loadContractDetail(record.id, { keepOpen: false }) : Promise.resolve(),
                    ]);
                } catch (error) {
                    messageApi.error(getErrorMessage(error, `${nextAction}合同失败`));
                }
            },
        });
    };

    const fillContractForm = (contract) => {
        setEditingContract(contract);
        contractForm.setFieldsValue({
            contract_no: contract.contract_no,
            customer_id: contract.customer_id,
            contract_kind: contract.contract_kind || "TIER",
            start_date: contract.start_date ? dayjs(contract.start_date) : null,
            end_date: contract.end_date ? dayjs(contract.end_date) : null,
            currency: contract.currency,
            base_fuel_price_per_mt: contract.base_fuel_price_per_mt == null ? null : Number(contract.base_fuel_price_per_mt),
            acmi_registration: contract.acmi_registration || null,
            acmi_lump_sum: contract.acmi_lump_sum == null ? null : Number(contract.acmi_lump_sum),
            frequency_text: contract.frequency_text || null,
            aircraft_text: parseAircraftText(contract.aircraft_text),
            status: contract.status,
            remark: contract.remark || null,
        });
    };

    const openContractDetail = (contractId) => {
        rememberContractListScroll();
        navigate(`/sales/charter-contracts/${contractId}`);
    };

    const contractFormFilledRef = useRef(null);
    useEffect(() => {
        if (!routeContractId) {
            contractFormFilledRef.current = null;
            setContractHeaderEditing(false);
            return;
        }
        setContractHeaderEditing(false);
        setDetailTabKey("prices");
        setContractBookings([]);
        setContractBookingsLoaded(false);
        setContractBookingsQuery({ page: 1, pageSize: 20, total: 0 });
        loadContractDetail(routeContractId);
    }, [routeContractId, loadContractDetail]);

    useLayoutEffect(() => {
        if (routeContractId) return undefined;
        setKeepContractList(true);
        const restoreScroll = () => {
            const body = contractTableRef.current?.querySelector(".ant-table-body");
            if (body) body.scrollTop = contractTableScrollRef.current;
        };
        restoreScroll();
        const frame = window.requestAnimationFrame(restoreScroll);
        return () => window.cancelAnimationFrame(frame);
    }, [routeContractId]);

    useEffect(() => {
        if (!routeContractId || !detailContract || detailContract.id !== routeContractId) return;
        const stamp = `${detailContract.id}:${detailContract.updated_at || ""}`;
        if (contractFormFilledRef.current === stamp) return;
        contractFormFilledRef.current = stamp;
        fillContractForm(detailContract);
    }, [routeContractId, detailContract]);

    const handleDetailTabChange = (key) => {
        setDetailTabKey(key);
        if (key === "bookings" && detailContract?.id && !contractBookingsLoaded && !contractBookingsLoading) {
            loadContractBookings(detailContract.id);
        }
    };

    const openCreateRoutePriceModal = () => {
        setEditingRoutePrice(null);
        routePriceForm.resetFields();
        routePriceForm.setFieldsValue({
            currency: detailContract?.currency || "USD",
            status: "ACTIVE",
            effective_date: dayjs(),
            price_rows: [],
        });
        setRoutePriceModalOpen(true);
        loadRoutePoolOptions();
    };

    const handleRoutePoolSelect = (routeId) => {
        const matched = routePoolOptions.find((item) => item.value === routeId);
        if (matched) {
            routePriceForm.setFieldsValue({
                origin_airport: matched.origin_airport,
                destination_airport: matched.destination_airport,
            });
        }
    };

    const openEditRoutePriceModal = (record) => {
        setEditingRoutePrice(record);
        routePriceForm.setFieldsValue({
            origin_airport: record.origin_airport,
            destination_airport: record.destination_airport,
            aircraft_type: record.aircraft_type || null,
            price_rows: routePriceRowsFromRecord(record),
            currency: record.currency,
            effective_date: record.effective_date ? dayjs(record.effective_date) : null,
            expiry_date: record.expiry_date ? dayjs(record.expiry_date) : null,
            status: record.status,
        });
        setRoutePriceModalOpen(true);
    };

    const handleRoutePriceSubmit = async () => {
        if (!detailContract?.id) {
            return;
        }
        try {
            const values = await routePriceForm.validateFields();
            const rows = values.price_rows || [];
            const rowOf = (kind) => rows.find((item) => item?.kind === kind);
            const either = rowOf("UNDIFFERENTIATED");
            const direct = rowOf("DIRECT");
            const stop = rowOf("WITH_STOP");
            const payload = {
                aircraft_type: values.aircraft_type || null,
                undifferentiated_unit_price: either ? either.unit_price : null,
                direct_unit_price: direct ? direct.unit_price : null,
                with_stop_unit_price: stop ? stop.unit_price : null,
                undifferentiated_status: either ? "ACTIVE" : "INACTIVE",
                direct_status: direct ? "ACTIVE" : "INACTIVE",
                with_stop_status: stop ? "ACTIVE" : "INACTIVE",
                currency: normalizeCurrency(values.currency),
                effective_date: serializeDate(values.effective_date),
                expiry_date: serializeDate(values.expiry_date),
                status: values.status,
            };
            setRoutePriceSubmitting(true);
            if (editingRoutePrice) {
                await salesRevenueApi.updateRoutePrice(editingRoutePrice.id, payload);
                messageApi.success("合同航线已保存");
            } else {
                await salesRevenueApi.createRoutePrice(detailContract.id, {
                    ...payload,
                    origin_airport: normalizeAirport(values.origin_airport),
                    destination_airport: normalizeAirport(values.destination_airport),
                });
                messageApi.success("合同航线已添加");
            }
            setRoutePriceModalOpen(false);
            await Promise.all([loadContractDetail(detailContract.id, { keepOpen: false }), loadContracts()]);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, editingRoutePrice ? "保存合同航线失败" : "添加合同航线失败"));
        } finally {
            setRoutePriceSubmitting(false);
        }
    };

    const handleDeleteRoutePrice = (record) => {
        modalApi.confirm({
            title: "确认停用合同航线",
            icon: <ExclamationCircleOutlined />,
            content: `确定停用 ${record.origin_airport} → ${record.destination_airport} 吗？已经钉住它的订单不会改用别的航线。`,
            okText: "停用",
            cancelText: "取消",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await salesRevenueApi.deleteRoutePrice(record.id);
                    messageApi.success("合同航线已停用");
                    if (detailContract?.id) {
                        await Promise.all([loadContractDetail(detailContract.id, { keepOpen: false }), loadContracts()]);
                    }
                } catch (error) {
                    messageApi.error(getErrorMessage(error, "停用合同航线失败"));
                }
            },
        });
    };

    const makeFuelTierRow = useCallback(
        (overrides = {}) => {
            fuelTierRowSeq.current += 1;
            return {
                key: `tier-${Date.now()}-${fuelTierRowSeq.current}`,
                diff_min: null,
                diff_max: null,
                adjustment_amount: null,
                currency: detailContract?.currency || "USD",
                status: "ACTIVE",
                ...overrides,
            };
        },
        [detailContract?.currency]
    );

    const openCreateFuelTierModal = () => {
        ladderForm.resetFields();
        ladderForm.setFieldsValue({ start: 0, step: 100, count: 5, amount_start: 0, amount_step: 0 });
        setFuelTierRows([makeFuelTierRow(), makeFuelTierRow(), makeFuelTierRow()]);
        setFuelTierBatchOpen(true);
    };

    const addFuelTierRows = (count) => {
        setFuelTierRows((rows) => [...rows, ...Array.from({ length: count }, () => makeFuelTierRow())]);
    };

    const updateFuelTierRow = (key, field, value) => {
        setFuelTierRows((rows) => rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
    };

    const removeFuelTierRow = (key) => {
        setFuelTierRows((rows) => rows.filter((row) => row.key !== key));
    };

    const generateLadderRows = async () => {
        try {
            const values = await ladderForm.validateFields();
            const start = Number(values.start);
            const step = Number(values.step);
            const count = Number(values.count);
            const amountStart = Number(values.amount_start);
            const amountStep = Number(values.amount_step);
            if (!Number.isFinite(step) || step <= 0) {
                messageApi.warning("步长必须大于 0");
                return;
            }
            if (!Number.isInteger(count) || count <= 0) {
                messageApi.warning("档数必须为正整数");
                return;
            }
            const generated = Array.from({ length: count }, (_, index) => {
                const min = Number((start + index * step).toFixed(2));
                const max = Number((start + (index + 1) * step - 0.01).toFixed(2));
                const amount = Number((amountStart + index * amountStep).toFixed(2));
                return makeFuelTierRow({ diff_min: min, diff_max: max, adjustment_amount: amount });
            });
            setFuelTierRows(generated);
            messageApi.success(`已生成 ${count} 档，可继续在表格内微调`);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error("生成阶梯失败");
        }
    };

    const handleFuelTierBatchSubmit = async () => {
        if (!detailContract?.id) {
            return;
        }
        const filled = fuelTierRows.filter(
            (row) => row.diff_min != null && row.diff_max != null && row.adjustment_amount != null
        );
        if (filled.length === 0) {
            messageApi.warning("请至少填写一行完整的阶梯（下限、上限、调整金额）");
            return;
        }
        const incomplete = fuelTierRows.some(
            (row) =>
                (row.diff_min != null || row.diff_max != null || row.adjustment_amount != null) &&
                (row.diff_min == null || row.diff_max == null || row.adjustment_amount == null)
        );
        if (incomplete) {
            messageApi.warning("存在未填完整的行，请补齐或删除后再提交");
            return;
        }
        const invalidRange = filled.find((row) => Number(row.diff_max) < Number(row.diff_min));
        if (invalidRange) {
            messageApi.warning("存在上限小于下限的行，请检查");
            return;
        }
        try {
            setFuelTierBatchSubmitting(true);
            await salesRevenueApi.createFuelTiersBatch(detailContract.id, {
                tiers: filled.map((row) => ({
                    diff_min: row.diff_min,
                    diff_max: row.diff_max,
                    adjustment_amount: row.adjustment_amount,
                    currency: normalizeCurrency(row.currency),
                    status: row.status,
                })),
            });
            messageApi.success(`已批量创建 ${filled.length} 条燃油阶梯`);
            setFuelTierBatchOpen(false);
            await loadContractDetail(detailContract.id, { keepOpen: false });
        } catch (error) {
            messageApi.error(getErrorMessage(error, "批量创建燃油阶梯失败"));
        } finally {
            setFuelTierBatchSubmitting(false);
        }
    };

    const openEditFuelTierModal = (record) => {
        setEditingFuelTier(record);
        fuelTierForm.setFieldsValue({
            diff_min: Number(record.diff_min),
            diff_max: Number(record.diff_max),
            adjustment_amount: Number(record.adjustment_amount),
            currency: record.currency,
            status: record.status,
        });
        setFuelTierModalOpen(true);
    };

    const handleFuelTierSubmit = async () => {
        if (!detailContract?.id || !editingFuelTier) {
            return;
        }
        try {
            const values = await fuelTierForm.validateFields();
            const payload = {
                diff_min: values.diff_min,
                diff_max: values.diff_max,
                adjustment_amount: values.adjustment_amount,
                currency: normalizeCurrency(values.currency),
                status: values.status,
            };
            setFuelTierSubmitting(true);
            await salesRevenueApi.updateFuelTier(editingFuelTier.id, payload);
            messageApi.success("燃油阶梯更新成功");
            setFuelTierModalOpen(false);
            await loadContractDetail(detailContract.id, { keepOpen: false });
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, "更新燃油阶梯失败"));
        } finally {
            setFuelTierSubmitting(false);
        }
    };

    const handleToggleFuelTierStatus = async (record) => {
        const nextStatus = record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        const actionLabel = nextStatus === "ACTIVE" ? "启用" : "停用";
        try {
            await salesRevenueApi.updateFuelTier(record.id, { status: nextStatus });
            messageApi.success(`燃油阶梯已${actionLabel}`);
            if (detailContract?.id) {
                await loadContractDetail(detailContract.id, { keepOpen: false });
            }
        } catch (error) {
            messageApi.error(getErrorMessage(error, `${actionLabel}燃油阶梯失败`));
        }
    };

    const openCreatePrepaymentModal = () => {
        if (!detailContract) {
            return;
        }
        prepaymentForm.resetFields();
        prepaymentForm.setFieldsValue({
            contract_id: detailContract.id,
            customer_id: detailContract.customer_id,
            currency: detailContract.currency || "USD",
            received_date: dayjs(),
        });
        setPrepaymentModalOpen(true);
    };

    const handleCreatePrepayment = async () => {
        if (!detailContract) {
            return;
        }
        try {
            const values = await prepaymentForm.validateFields();
            setPrepaymentSubmitting(true);
            await salesRevenueApi.createPrepayment({
                contract_id: detailContract.id,
                customer_id: detailContract.customer_id,
                amount: values.amount,
                currency: normalizeCurrency(values.currency),
                received_date: serializeDate(values.received_date),
                remark: values.remark?.trim() || null,
            });
            messageApi.success("预付款录入成功");
            setPrepaymentModalOpen(false);
            await loadContractDetail(detailContract.id, { keepOpen: false });
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, "录入预付款失败"));
        } finally {
            setPrepaymentSubmitting(false);
        }
    };

    const handleGenerateBaseReceivable = (record, options = {}) => {
        const { silent = false } = options;
        modalApi.confirm({
            title: "确认生成基础运价应收",
            icon: <ExclamationCircleOutlined />,
            content: `确定要为 ${record.booking_no || "该 CB 订单"} 生成基础运价应收吗？`,
            okText: "生成应收",
            cancelText: "取消",
            onOk: async () => {
                try {
                    const response = await salesRevenueApi.generateBaseReceivable(record.booking_id || record.charter_booking_id);
                    if (!silent) {
                        messageApi.success(response.data?.message || "基础运价应收生成成功");
                    }
                    await loadReceivables();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, "基础运价应收生成失败"));
                }
            },
        });
    };

    const openBookingDetail = async (group) => {
        setBookingDetailKey(group.booking_id);
        setBookingDetailOpen(true);
        setBookingFlights([]);
        setBookingFlightsLoading(true);
        try {
            const response = await salesRevenueApi.listBookingFlights(group.booking_id);
            setBookingFlights(response.data?.items || []);
        } catch (error) {
            messageApi.error(getErrorMessage(error, "获取订单航班失败"));
        } finally {
            setBookingFlightsLoading(false);
        }
    };

    const handleReceivableRematch = (record, options = {}) => {
        const { silent = false, force = false, manualAdjusted = null } = options;
        modalApi.confirm({
            title: force ? "存在手工调整，确认强制重匹配？" : "确认重新匹配基础运价应收",
            icon: force ? <WarningOutlined style={{ color: "#faad14" }} /> : <ExclamationCircleOutlined />,
            content: force
                ? renderManualAdjustedList(manualAdjusted, "强制重匹配")
                : `确定要重新匹配 ${record.booking_no || record.receivable_no} 的基础运价应收吗？`,
            okText: force ? "强制重匹配（清空手工值）" : "重新匹配",
            okButtonProps: force ? { danger: true } : undefined,
            cancelText: force ? "保留手工值（取消）" : "取消",
            onOk: async () => {
                try {
                    await salesRevenueApi.rematchBaseReceivable(record.charter_booking_id, { force });
                    if (!silent) {
                        messageApi.success("基础运价应收已重新匹配");
                    }
                    await Promise.all([
                        loadReceivables(),
                        receivableDetail?.id === record.id ? loadReceivableDetail(record.id, { keepOpen: false }) : Promise.resolve(),
                    ]);
                } catch (error) {
                    const conflict = getManualAdjustedConflict(error);
                    if (!force && conflict) {
                        handleReceivableRematch(record, { ...options, force: true, manualAdjusted: conflict });
                        return;
                    }
                    messageApi.error(getErrorMessage(error, "重新匹配基础运价应收失败"));
                }
            },
        });
    };

    const handleUnbindContract = async (record) => {
        try {
            await salesRevenueApi.unassignBookingContract(record.id);
            messageApi.success("已解绑合同");
            if (detailContract?.id) {
                await loadContractBookings(detailContract.id);
            }
        } catch (error) {
            const detail = error?.response?.data?.detail;
            messageApi.error(UNBIND_ERROR_TEXT[detail] || getErrorMessage(error, "解绑合同失败"));
        }
    };

    const openRebindContractModal = async (record) => {
        setRebindTarget(record);
        bookingRebindForm.resetFields();
        bookingRebindForm.setFieldsValue({ contract_id: record.contract_id || undefined });
        setRebindModalOpen(true);
        if (!record.customer_id) {
            setRebindContractOptions([]);
            return;
        }
        setRebindContractOptionsLoading(true);
        try {
            const response = await salesRevenueApi.listContracts({ customerId: record.customer_id, pageSize: 200 });
            setRebindContractOptions(response.data?.items || []);
        } catch (error) {
            setRebindContractOptions([]);
            messageApi.error(getErrorMessage(error, "加载客户合同失败"));
        } finally {
            setRebindContractOptionsLoading(false);
        }
    };

    const closeRebindModal = () => {
        setRebindModalOpen(false);
        setRebindTarget(null);
        setRebindContractOptions([]);
    };

    const handleRebindContract = async () => {
        if (!rebindTarget) {
            return;
        }
        try {
            const values = await bookingRebindForm.validateFields();
            if (values.contract_id === rebindTarget.contract_id) {
                messageApi.info("所选合同与当前合同一致，未改绑");
                return;
            }
            setRebindSubmitting(true);
            const response = await salesRevenueApi.assignBookingCustomer(rebindTarget.charter_booking_id, {
                customer_id: rebindTarget.customer_id,
                contract_id: values.contract_id,
            });
            messageApi.success(response.data?.message || "已改绑合同");
            closeRebindModal();
            await Promise.all([
                loadReceivables(),
                receivableDetail?.id === rebindTarget.id ? loadReceivableDetail(rebindTarget.id, { keepOpen: false }) : Promise.resolve(),
            ]);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            const detail = error?.response?.data?.detail;
            messageApi.error(UNBIND_ERROR_TEXT[detail] || getErrorMessage(error, "改绑合同失败"));
        } finally {
            setRebindSubmitting(false);
        }
    };

    const handleReceivableRecalculate = (record, options = {}) => {
        const { silent = false, force = false, manualAdjusted = null } = options;
        modalApi.confirm({
            title: force ? "存在手工调整，确认强制重算？" : "确认执行联动重算",
            icon: force ? <WarningOutlined style={{ color: "#faad14" }} /> : <ExclamationCircleOutlined />,
            content: force
                ? renderManualAdjustedList(manualAdjusted, "强制重算")
                : `将重新校验 ${record.booking_no || record.receivable_no || "该 CB 订单"} 的基础运价、燃油调整；如果订单已取消，还会同步作废应收并刷新取消订单抵扣余额。`,
            okText: force ? "强制重算（清空手工值）" : "执行重算",
            okButtonProps: force ? { danger: true } : undefined,
            cancelText: force ? "保留手工值（取消）" : "取消",
            onOk: async () => {
                try {
                    const response = await salesRevenueApi.recalculateBookingReceivables(record.charter_booking_id || record.booking_id, { force });
                    const result = response.data?.data || {};
                    const successMessage =
                        result.action === "BOOKING_CANCELLED"
                            ? result.cancelled_credit
                                ? "订单已取消，应收已作废并刷新取消订单抵扣余额"
                                : "订单已取消，应收已作废"
                            : response.data?.message || "订单应收联动重算完成";
                    if (!silent) {
                        messageApi.success(successMessage);
                    }
                    await Promise.all([
                        loadReceivables(),
                        loadInvoices(),
                        receivableDetail?.id === record.id ? loadReceivableDetail(record.id, { keepOpen: false }) : Promise.resolve(),
                    ]);
                } catch (error) {
                    const conflict = getManualAdjustedConflict(error);
                    if (!force && conflict) {
                        handleReceivableRecalculate(record, { ...options, force: true, manualAdjusted: conflict });
                        return;
                    }
                    messageApi.error(getErrorMessage(error, "订单应收联动重算失败"));
                }
            },
        });
    };

    const openAdjustAmountModal = (record) => {
        setAdjustAmountTarget(record);
        adjustAmountForm.resetFields();
        adjustAmountForm.setFieldsValue({
            amount: null,
            manual_adjust_reason: "",
        });
        setAdjustAmountModalOpen(true);
    };

    const handleAdjustAmountSubmit = async () => {
        try {
            const values = await adjustAmountForm.validateFields();
            setAdjustAmountSubmitting(true);
            await salesRevenueApi.adjustReceivableAmount(adjustAmountTarget.id, {
                amount: values.amount,
                manual_adjust_reason: values.manual_adjust_reason.trim(),
            });
            messageApi.success("应收金额已调整");
            setAdjustAmountModalOpen(false);
            setAdjustAmountTarget(null);
            const bookingGroup = (receivableList.items || []).find(
                (g) => g.booking_id === adjustAmountTarget.charter_booking_id,
            );
            await Promise.all([
                loadReceivables(),
                bookingDetailOpen && bookingGroup ? openBookingDetail(bookingGroup) : Promise.resolve(),
                receivableDetail?.id === adjustAmountTarget.id ? loadReceivableDetail(adjustAmountTarget.id, { keepOpen: true }) : Promise.resolve(),
            ]);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, "应收金额调整失败"));
        } finally {
            setAdjustAmountSubmitting(false);
        }
    };

    const openExtraFeeModal = (record = null) => {
        if (!receivableContractOptions.length) {
            loadReceivableContracts();
        }
        const context = record || null;
        setExtraFeeContext(context);
        extraFeeForm.resetFields();
        extraFeeForm.setFieldsValue({
            booking_id: context?.charter_booking_id || null,
            booking_no: context?.booking_no || "",
            contract_id: context?.contract_id || null,
            extra_fee_type: "DEICING",
            amount: null,
            currency: context?.currency || "USD",
            billing_date: context?.flight_date_snapshot ? dayjs(context.flight_date_snapshot) : dayjs(),
            billing_timezone: context?.billing_timezone || "UTC",
            manual_adjust_reason: "",
        });
        setExtraFeeModalOpen(true);
    };

    const handleCreateExtraFeeReceivable = async () => {
        try {
            const values = await extraFeeForm.validateFields();
            setExtraFeeSubmitting(true);
            const response = await salesRevenueApi.createExtraFeeReceivable({
                booking_id: values.booking_id || extraFeeContext?.charter_booking_id || null,
                booking_no: values.booking_no?.trim() || extraFeeContext?.booking_no || null,
                contract_id: values.contract_id || null,
                extra_fee_type: values.extra_fee_type,
                amount: values.amount,
                currency: normalizeCurrency(values.currency),
                billing_date: serializeDate(values.billing_date),
                billing_timezone: values.billing_timezone || "UTC",
                manual_adjust_reason: values.manual_adjust_reason?.trim(),
            });
            messageApi.success(response.data?.message || "额外费用应收新增成功");
            setExtraFeeModalOpen(false);
            setExtraFeeContext(null);
            await loadReceivables();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, "额外费用应收新增失败"));
        } finally {
            setExtraFeeSubmitting(false);
        }
    };

    const openBatchGenerateModal = () => {
        batchGenerateForm.resetFields();
        batchGenerateForm.setFieldsValue({
            period: dayjs(),
            contract_id: null,
            dryRun: true,
        });
        setBatchGenerateResult(null);
        setBatchGenerateModalOpen(true);
    };

    const handleBatchGenerateSubmit = async () => {
        try {
            const values = await batchGenerateForm.validateFields();
            setBatchGenerateSubmitting(true);
            const payload = {
                period: serializeMonth(values.period),
                contract_id: values.contract_id || null,
                dryRun: Boolean(values.dryRun),
            };
            const response = await salesRevenueApi.generateBaseReceivablesBatch(payload);
            const result = response.data;
            setBatchGenerateResult(result);
            if (payload.dryRun) {
                messageApi.success("基础运价补生成预览已完成");
            } else {
                messageApi.success(
                    `批量补生成完成：新增 ${result.summary.create_count} 条，异常 ${result.summary.exception_count} 条，复用 ${result.summary.reused_count} 条`,
                );
                await loadReceivables();
            }
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, "基础运价批量补生成失败"));
        } finally {
            setBatchGenerateSubmitting(false);
        }
    };

    const openFuelCalcModal = async () => {
        if (!receivableContractOptions.length) {
            await loadReceivableContracts();
        }
        fuelCalcForm.resetFields();
        // 预填筛选里的合同，但只接受阶梯合同（按段油耗合同走「按段油耗」页签）
        const presetContract = receivableContractOptions.find(
            (item) => item.id === receivableFilters.contractId && (item.contract_kind || "TIER") === "TIER",
        );
        fuelCalcForm.setFieldsValue({
            target_month: receivableFilters.flightMonth || dayjs(),
            contract_id: presetContract ? presetContract.id : null,
        });
        setFuelCalcResult(null);
        setFuelCalcModalOpen(true);
    };

    const handleFuelCalcPreview = async () => {
        try {
            const values = await fuelCalcForm.validateFields();
            setFuelCalcPreviewLoading(true);
            const response = await salesRevenueApi.previewFuelSurcharge({
                contract_id: values.contract_id,
                target_month: serializeMonth(values.target_month),
            });
            const result = response.data;
            setFuelCalcResult(result);
            if (!result.is_ready) {
                messageApi.warning(result.message || "参考月份 IATA 油价未齐，当前不能生成燃油调整应收");
            } else if (result.is_first_contract_month) {
                messageApi.info(result.message || "合同首月不生成燃油调整");
            } else {
                messageApi.success(result.message || "燃油调整预览已完成");
            }
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, "燃油调整预览失败"));
        } finally {
            setFuelCalcPreviewLoading(false);
        }
    };

    const handleFuelCalcGenerate = () => {
        if (!fuelCalcResult?.contract_id || !fuelCalcResult?.target_month || !fuelCalcResult?.summary?.ready_count) {
            return;
        }
        modalApi.confirm({
            title: "确认生成燃油调整应收",
            icon: <ExclamationCircleOutlined />,
            content: `将为 ${fuelCalcResult.target_month} 生成 ${fuelCalcResult.summary.ready_count} 笔燃油调整应收，合计 ${formatMoney(
                fuelCalcResult.summary.total_adjustment_amount,
            )} ${fuelCalcResult.matched_tier?.currency || ""}。`,
            okText: "确认生成",
            cancelText: "取消",
            onOk: async () => {
                try {
                    setFuelCalcGenerating(true);
                    const response = await salesRevenueApi.generateFuelSurcharge({
                        contract_id: fuelCalcResult.contract_id,
                        target_month: fuelCalcResult.target_month,
                    });
                    const result = response.data?.data || {};
                    const skipped = result.manual_adjusted_skipped || [];
                    let msg =
                        response.data?.message ||
                        `已生成 ${result.created_count || 0} 条燃油调整应收，总计 ${formatMoney(result.total_adjustment_amount || 0)}`;
                    if (skipped.length > 0) {
                        msg += `（${skipped.length} 个已手工调整的订单被跳过）`;
                        messageApi.warning(msg, 6);
                    } else {
                        messageApi.success(msg);
                    }
                    setFuelCalcModalOpen(false);
                    setFuelCalcResult(null);
                    await Promise.all([loadReceivables(), loadInvoices()]);
                } catch (error) {
                    messageApi.error(getErrorMessage(error, "生成燃油调整应收失败"));
                } finally {
                    setFuelCalcGenerating(false);
                }
            },
        });
    };

    const getReceivableIdsForInvoiceType = useCallback(
        (invoiceType) => {
            const typeMap = {
                BASE_WEEKLY: ["BASE_FARE"],
                FUEL_MONTHLY: ["FUEL_SURCHARGE"],
                EXTRA_FEE: ["EXTRA_FEE"],
                MIXED: ["BASE_FARE", "FUEL_SURCHARGE", "EXTRA_FEE"],
            };
            const allowedTypes = typeMap[invoiceType] || typeMap.MIXED;
            return receivableDetailItems
                .filter((item) => item.status === "PENDING_INVOICE" && allowedTypes.includes(item.type))
                .map((item) => item.id);
        },
        [receivableDetailItems],
    );

    const openBatchInvoiceModal = (invoiceType = "MIXED") => {
        const candidateIds = getReceivableIdsForInvoiceType(invoiceType);
        if (!candidateIds.length) {
            messageApi.warning("当前筛选范围内没有可开票的应收项");
            return;
        }
        invoiceForm.resetFields();
        invoiceForm.setFieldsValue({
            invoice_type: invoiceType,
            invoice_date: dayjs(),
            remark: null,
        });
        setInvoiceCandidateType(invoiceType);
        setSelectedInvoiceReceivableIds(candidateIds);
        setInvoiceModalOpen(true);
    };

    const handleInvoiceTypeChange = (value) => {
        setInvoiceCandidateType(value);
        setSelectedInvoiceReceivableIds(getReceivableIdsForInvoiceType(value));
    };

    const handleCreateInvoice = async () => {
        if (!selectedInvoiceReceivableIds.length) {
            messageApi.warning("请至少选择一条应收");
            return;
        }
        try {
            const values = await invoiceForm.validateFields();
            setInvoiceSubmitting(true);
            await salesRevenueApi.createInvoiceBatch({
                invoice_no: values.invoice_no.trim(),
                invoice_type: values.invoice_type,
                invoice_date: serializeDate(values.invoice_date),
                receivable_ids: selectedInvoiceReceivableIds,
                remark: values.remark?.trim() || null,
            });
            messageApi.success("发票创建成功");
            setInvoiceModalOpen(false);
            setSelectedInvoiceReceivableIds([]);
            await Promise.all([loadInvoices(), loadReceivables()]);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, "发票创建失败"));
        } finally {
            setInvoiceSubmitting(false);
        }
    };

    const openInvoiceDetail = async (record) => {
        await loadInvoiceDetail(record.id);
    };

    const handleVoidInvoice = (record) => {
        modalApi.confirm({
            title: "确认作废发票",
            icon: <ExclamationCircleOutlined />,
            content: `确定要作废发票 ${record.invoice_no} 吗？`,
            okText: "作废",
            cancelText: "取消",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await salesRevenueApi.voidInvoice(record.id);
                    messageApi.success("发票已作废");
                    await Promise.all([
                        loadInvoices(),
                        loadReceivables(),
                        invoiceDetail?.id === record.id ? loadInvoiceDetail(record.id, { keepOpen: false }) : Promise.resolve(),
                    ]);
                } catch (error) {
                    messageApi.error(getErrorMessage(error, "作废发票失败"));
                }
            },
        });
    };

    const openPaymentModal = async (record) => {
        setPaymentInvoice(record);
        paymentForm.resetFields();
        paymentForm.setFieldsValue({
            payment_date: dayjs(),
            amount: getOutstandingAmount(record.total_amount, record.paid_amount) || Number(record.total_amount || 0),
            source_type: "CUSTOMER_PAYMENT",
            payment_method: "WIRE",
            credit_usages: [],
            remark: null,
        });
        setPaymentModalOpen(true);
        await loadCustomerCredits(record.customer_id);
    };

    const handleCreatePayment = async () => {
        if (!paymentInvoice?.id) {
            return;
        }
        try {
            const values = await paymentForm.validateFields();
            const creditUsages = (values.credit_usages || [])
                .filter((item) => item?.credit_ledger_id && Number(item.amount) > 0)
                .map((item) => ({
                    credit_ledger_id: item.credit_ledger_id,
                    amount: item.amount,
                }));
            setPaymentSubmitting(true);
            await salesRevenueApi.createInvoicePayment(paymentInvoice.id, {
                amount: values.amount,
                payment_date: serializeDate(values.payment_date),
                payment_method: values.payment_method,
                source_type: values.source_type,
                credit_usages: creditUsages,
                remark: values.remark?.trim() || null,
            });
            messageApi.success("收款录入成功");
            setPaymentModalOpen(false);
            await Promise.all([
                loadInvoices(),
                loadReceivables(),
                invoiceDetail?.id === paymentInvoice.id ? loadInvoiceDetail(paymentInvoice.id, { keepOpen: false }) : Promise.resolve(),
            ]);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(getErrorMessage(error, "录入收款失败"));
        } finally {
            setPaymentSubmitting(false);
        }
    };

    const handleExportReceivables = () => {
        if (!receivableDetailItems.length) {
            messageApi.warning("当前没有可导出的应收数据");
            return;
        }
        downloadCsv(
            `sales-receivables-${dayjs().format("YYYYMMDD-HHmmss")}.csv`,
            [
                ["CB订单", "应收编号", "客户", "合同编号", "类型", "状态", "应收金额", "已收金额", "计费日期", "去程航线", "异常码", "异常说明"],
                ...receivableDetailItems.map((item) => [
                    item.booking_no || "",
                    item.receivable_no || "",
                    item.customer_name || "",
                    item.contract_no || "",
                    RECEIVABLE_TYPE_TEXT[item.type] || item.type || "",
                    RECEIVABLE_STATUS_TEXT[item.status] || item.status || "",
                    item.amount ?? "",
                    item.paid_amount ?? "",
                    item.flight_date_snapshot || "",
                    item.route_display_snapshot || "",
                    item.exception_code || "",
                    item.exception_message || "",
                ]),
            ],
        );
        messageApi.success("当前页应收数据已导出");
    };

    const {
        contractColumns,
        priceColumns,
        fuelTierColumns,
        fuelTierBatchColumns,
        prepaymentColumns,
        contractBookingColumns,
        bookingColumns,
        bookingReceivableDetailColumns,
        invoiceColumns,
        invoiceCandidateColumns,
        invoiceReceivableColumns,
        invoicePaymentColumns,
        paymentAllocationColumns,
        receivableFlightLegColumns,
        bookingFlightColumns,
        receivableEventColumns,
        batchGenerateColumns,
        fuelCalcColumns,
        fuelCalcTierColumns,
    } = createSalesRevenueColumns({
        canWriteContract,
        canWriteReceivable,
        canWritePayment,
        canWriteInvoice,
        canGenerateFuel,
        currencyOptions,
        invoiceDetail,
        batchGenerateResult,
        fuelCalcResult,
        openContractDetail,
        handleContractStatusChange,
        openEditRoutePriceModal,
        handleDeleteRoutePrice,
        openEditFuelTierModal,
        handleToggleFuelTierStatus,
        updateFuelTierRow,
        removeFuelTierRow,
        handleUnbindContract,
        handleReceivableRecalculate,
        openExtraFeeModal,
        handleGenerateBaseReceivable,
        openFuelCalcModal,
        handleReceivableRematch,
        openRebindContractModal,
        openAdjustAmountModal,
        openInvoiceDetail,
        openPaymentModal,
        handleVoidInvoice,
        detailContractKind: detailContract?.contract_kind || null,
        bookingDetailKind:
            (receivableList.items || []).find((group) => group.booking_id === bookingDetailKey)?.contract_kind || null,
        openAdHocAmountModal: (record) => setAdHocPanelBooking(record),
    });

    const buildBookingDetailRows = (group) => {
        const rows = (group.receivables || []).map((item) => ({
            ...item,
            _rowKey: item.id,
            booking_no: group.booking_no,
            charter_booking_id: group.booking_id,
            customer_id: group.customer_id,
            customer_name: group.customer_name,
            contract_id: group.contract_id,
            contract_no: group.contract_no,
            route_display_snapshot: group.route_display,
            aircraft_type_snapshot: group.aircraft_type,
            flight_date_snapshot: group.flight_date,
            billing_timezone: "UTC",
        }));
        const result = [];
        if (!group.summary?.has_base_fare) {
            result.push({
                _rowKey: `ph-base-${group.booking_id}`,
                _placeholder: true,
                type: "BASE_FARE",
                charter_booking_id: group.booking_id,
                booking_no: group.booking_no,
            });
        }
        result.push(...rows);
        if (!group.summary?.has_fuel_surcharge) {
            result.push({
                _rowKey: `ph-fuel-${group.booking_id}`,
                _placeholder: true,
                type: "FUEL_SURCHARGE",
                charter_booking_id: group.booking_id,
                booking_no: group.booking_no,
            });
        }
        return result;
    };

    const renderRoutesSection = () => (
        <>
            <div className="page-header-compact">
                <h3 className="page-title-compact">销售航线池</h3>
                {canWriteContract ? (
                    <div className="page-action-compact">
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateRouteModal}>
                            新增 O-D 航线
                        </Button>
                    </div>
                ) : null}
            </div>

            <Alert
                type="info"
                showIcon
                className="sales-revenue-alert"
                message="这里维护销售应收使用的去程 O-D 航线池。新航线通常在合同录入航线价格时自动同步到航线池。"
            />

            <div className="sales-revenue-filter-bar">
                <div className="sales-revenue-filter-item">
                    <label>始发站</label>
                    <Select
                        allowClear
                        value={routeFilters.originAirport || undefined}
                        placeholder="全部"
                        options={routeAirportOptions}
                        onChange={(value) => setRouteFilters((prev) => ({ ...prev, originAirport: value || "" }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>目的地</label>
                    <Select
                        allowClear
                        value={routeFilters.destinationAirport || undefined}
                        placeholder="全部"
                        options={routeAirportOptions}
                        onChange={(value) => setRouteFilters((prev) => ({ ...prev, destinationAirport: value || "" }))}
                    />
                </div>
                <div className="sales-revenue-filter-item sales-revenue-filter-item-wide">
                    <label>航线名称</label>
                    <Input
                        value={routeFilters.keyword}
                        placeholder="如 香港-列日 / HKG-LGG"
                        onChange={(event) => setRouteFilters((prev) => ({ ...prev, keyword: event.target.value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>状态</label>
                    <Select value={routeFilters.status} options={ROUTE_STATUS_OPTIONS} onChange={(value) => setRouteFilters((prev) => ({ ...prev, status: value }))} />
                </div>
                <div className="sales-revenue-filter-actions">
                    <Button
                        type="primary"
                        onClick={() => {
                            setRoutePagination((prev) => ({ ...prev, page: 1 }));
                            loadRoutes();
                        }}
                    >
                        搜索
                    </Button>
                    <Button
                        onClick={() => {
                            setRouteFilters({
                                originAirport: "",
                                destinationAirport: "",
                                keyword: "",
                                status: "ACTIVE",
                            });
                            setRoutePagination((prev) => ({ ...prev, page: 1 }));
                        }}
                    >
                        重置
                    </Button>
                </div>
            </div>

            <div className="sales-revenue-card">
                <div className="sales-revenue-card-header">
                    <span>去程航线列表</span>
                    <span className="sales-revenue-card-meta">
                        共 {routeStats.total} 条 · {routeStats.active} 条启用 · {routeStats.inactive} 条停用
                    </span>
                </div>
                <div className="sales-revenue-card-body">
                    {routeLoading ? (
                        <div className="sales-revenue-loading">
                            <Spin />
                        </div>
                    ) : routeList.items.length ? (
                        <>
                            <div className="sales-revenue-route-list">
                                {routeList.items.map((item) => (
                                    <div key={item.id} className={`sales-revenue-route-card ${item.status === "INACTIVE" ? "is-disabled" : ""}`}>
                                        <div className="sales-revenue-route-main">
                                            <div className="sales-revenue-route-code">
                                                {item.origin_airport} - {item.destination_airport}
                                            </div>
                                        </div>
                                        <div className="sales-revenue-route-meta">
                                            <div>
                                                <span className="sales-revenue-meta-label">航线名称:</span> {item.name || "—"}
                                            </div>
                                            <div>
                                                <span className="sales-revenue-meta-label">历史经停:</span> {item.historical_stops?.length ? item.historical_stops.join(" / ") : "—"}
                                            </div>
                                            <div>
                                                <span className="sales-revenue-meta-label">航线代码:</span> {item.route_code}
                                            </div>
                                            <div>
                                                <span className="sales-revenue-meta-label">来源:</span> {ROUTE_SOURCE_TEXT[item.source_type] || item.source_type}
                                            </div>
                                        </div>
                                        <div className="sales-revenue-route-side">
                                            <SectionTag tone={getToneClass("route-status", item.status)}>{ROUTE_STATUS_TEXT[item.status] || item.status}</SectionTag>
                                            <SectionTag tone={getToneClass("route-source", item.source_type)}>{ROUTE_SOURCE_TEXT[item.source_type] || item.source_type}</SectionTag>
                                            <div className="sales-revenue-action-links">
                                                {canWriteContract ? (
                                                    <Button type="link" size="small" onClick={() => openEditRouteModal(item)}>
                                                        编辑
                                                    </Button>
                                                ) : null}
                                                {canWriteContract ? (
                                                    <Button type="link" size="small" danger={item.status === "ACTIVE"} onClick={() => handleRouteStatusChange(item)}>
                                                        {item.status === "ACTIVE" ? "停用" : "启用"}
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="sales-revenue-pagination">
                                <Pagination
                                    current={routePagination.page}
                                    pageSize={routePagination.pageSize}
                                    total={routeList.total}
                                    showSizeChanger
                                    onChange={(page, pageSize) => setRoutePagination({ page, pageSize })}
                                />
                            </div>
                        </>
                    ) : (
                        <Empty description="暂无航线数据" />
                    )}
                </div>
            </div>

        </>
    );

    const applyContractSearch = (patch = {}) => {
        setContractFilters((prev) => ({
            ...prev,
            ...patch,
            keyword: contractKeywordDraft.trim(),
        }));
    };

    const contractTourSteps = [
        {
            title: "合同筛选",
            description: "按客户、合同状态或关键词快速检索合同。输入条件后点「搜索」查找，右侧按钮重置筛选。",
            target: () => contractFilterRef.current,
        },
        {
            title: "合同列表与类型",
            description: "每行是一份销售合同。系统支持三种类型：「阶梯合同」固定航线价 + IATA 油价阶梯表计燃油；「按段油耗」固定航线价 + 逐段实际油耗计燃油；「一班一议」合同配航线价，燃油选手填。",
            target: () => contractTableRef.current,
        },
        {
            title: "合同详情",
            description: "点合同编号打开详情。预付款汇总和流水固定在上方。下面三个标签：合同航线、燃油、绑定订单。阶梯合同在燃油标签里维护阶梯；按段油耗和一班一议这里只看说明。按段油耗的挑段和填油耗在应收页的订单详情，一班一议的燃油在绑定订单里手填。",
            target: () => contractTableRef.current,
        },
        ...(canWriteContract
            ? [
                  {
                      title: "新建合同",
                      description: "新建一份客户合同，需填写合同编号、选择客户、设定合同类型与有效期。点「打开并演示」，进入弹窗逐字段带你填写。",
                      target: () => contractCreateBtnRef.current,
                      nextButtonProps: { children: "打开并演示" },
                  },
              ]
            : []),
    ];

    const contractModalGuideSteps = [
        {
            title: "合同编号 & 客户",
            description: "合同编号在草稿和生效中可以修改，用来把先飞后补的占位编号转正。已终止或已过期后不能再改。右侧选择签约客户，创建后不可更改。",
            target: () => contractFieldNoRef.current,
        },
        {
            title: "合同类型（关键）",
            description: "决定后续定价和燃油计算方式，创建后不可更改。「阶梯」按 IATA 油价区间给固定附加费；「按段油耗」逐段按实际油耗 × 油价差价；「一班一议」合同配航线价，不算燃油公式。",
            target: () => contractFieldKindRef.current,
        },
        {
            title: "有效期",
            description: "设定合同生效和失效日期。一班一议合同可留空，表示永久有效。",
            target: () => contractFieldDateRef.current,
        },
        {
            title: "基准油价",
            description: "只有阶梯合同用合同头这一个基准油价，而且可以不填。按段油耗的基准价按起飞机场，不在合同头必填。一班一议没有这个字段。",
            target: () => contractFieldFuelRef.current,
        },
        {
            title: "提交创建",
            description: "其余频次、机型概述、备注为可选。机型可多选，只给人看，不算价。确认后点「创建」，再进详情配航线价格。阶梯合同还要配燃油阶梯。",
        },
    ];

    const receivableTourSteps = [
        {
            title: "操作按钮",
            description: "「导出 CSV」导出当前筛选结果；「基础运价补生成」按月批量为已绑合同的 CB 订单生成应收；「燃油调整计算」按月预览并批量生成燃油附加费。",
            target: () => receivableActionsRef.current,
        },
        {
            title: "筛选条件",
            description: "按客户、合同、航线、状态、类型、航班月份、订单号等条件精确定位应收记录。异常筛选可快速发现价格匹配问题。",
            target: () => receivableFilterRef.current,
        },
        {
            title: "两种查看维度",
            description: "「订单维度」按 CB 订单聚合，适合核对应收和补生成；「发票维度」按发票聚合，适合跟踪开票、收款和回单处理。",
            target: () => receivableViewTabsRef.current,
        },
        {
            title: "待开票概览",
            description: "按基础运价、燃油调整、额外费用分类汇总待开票金额。金额卡片可直接点击，进入对应类型的批量开票。",
            target: () => receivableGuideRef.current,
        },
    ];

    const renderContractsSection = () => (
        <>
            <div className="page-header-compact">
                <h3 className="page-title-compact">销售合同</h3>
                <div className="page-action-compact">
                    <Space>
                        <Button icon={<QuestionCircleOutlined />} onClick={() => setContractTourOpen(true)}>
                            使用教程
                        </Button>
                        {canWriteContract ? (
                            <Button ref={contractCreateBtnRef} type="primary" icon={<PlusOutlined />} onClick={openCreateContractModal}>
                                新建合同
                            </Button>
                        ) : null}
                    </Space>
                </div>
            </div>

            <div ref={contractFilterRef} className="sales-revenue-filter-bar sales-revenue-contract-filter">
                <div className="sales-revenue-filter-item">
                    <label>客户</label>
                    <Select
                        allowClear
                        showSearch
                        loading={customersLoading}
                        style={{ width: contractCustomerFilterWidth }}
                        value={contractFilters.customerId || undefined}
                        placeholder="全部客户"
                        options={customerOptions}
                        optionFilterProp="label"
                        onChange={(value) => applyContractSearch({ customerId: value || "" })}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>状态</label>
                    <Select
                        style={{ width: contractStatusFilterWidth }}
                        value={contractFilters.status}
                        options={CONTRACT_STATUS_OPTIONS}
                        onChange={(value) => applyContractSearch({ status: value })}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>合同编号/客户</label>
                    <Input
                        style={{ width: contractKeywordFilterWidth }}
                        value={contractKeywordDraft}
                        placeholder="合同编号、客户名称或机型，回车搜索"
                        onChange={(event) => setContractKeywordDraft(event.target.value)}
                        onPressEnter={() => applyContractSearch()}
                    />
                </div>
                <div className="sales-revenue-filter-actions">
                    <Button type="primary" onClick={() => applyContractSearch()}>
                        搜索
                    </Button>
                    <Button
                        onClick={() => {
                            setContractKeywordDraft("");
                            setContractFilters({ keyword: "", customerId: "", status: "" });
                        }}
                    >
                        重置
                    </Button>
                </div>
            </div>

            <div ref={contractTableRef} className="sales-revenue-card">
                <div className="sales-revenue-card-header">
                    <span>合同列表</span>
                    <span className="sales-revenue-card-meta">
                        {contractList.total > 0 ? `已加载 ${contractList.items.length} / ${contractList.total} 条` : "暂无数据"}
                    </span>
                </div>
                <div className="sales-revenue-card-body sales-revenue-table-body">
                    <Table
                        className="sales-revenue-contract-table"
                        rowKey="id"
                        columns={contractColumns}
                        dataSource={contractList.items}
                        loading={contractLoading}
                        pagination={false}
                        sticky
                        onScroll={handleContractTableScroll}
                        scroll={{ y: "calc(100vh - 320px)" }}
                        footer={
                            contractList.total > 0
                                ? () => (
                                      <div className="sales-revenue-scroll-footer">
                                          {contractLoadingMore ? (
                                              <Spin size="small" />
                                          ) : contractHasMore ? (
                                              <Button type="link" onClick={loadMoreContracts}>
                                                  向下滚动或点击加载更多
                                              </Button>
                                          ) : (
                                              <span className="sales-revenue-card-meta">已加载全部 {contractList.total} 条</span>
                                          )}
                                      </div>
                                  )
                                : undefined
                        }
                    />
                </div>
            </div>
        </>
    );

    const renderReceivableInvoiceView = () => (
        <>
            <Alert
                type="info"
                showIcon
                className="sales-revenue-alert"
                message="客户付款和回单处理建议在发票维度完成：一张发票可关联多班应收，录入一次收款即可同步更新全部关联应收状态。"
            />

            <div className="sales-revenue-card sales-revenue-guide-card">
                <div className="sales-revenue-card-body">
                    <div className="sales-revenue-guide-grid sales-revenue-guide-grid-compact">
                        <div
                            className={`sales-revenue-guide-cell sales-revenue-guide-cell-green ${canWriteInvoice && receivableGuideStats.totalPendingAmount ? "is-clickable" : ""}`}
                            onClick={canWriteInvoice && receivableGuideStats.totalPendingAmount ? () => openBatchInvoiceModal("MIXED") : undefined}
                            role={canWriteInvoice && receivableGuideStats.totalPendingAmount ? "button" : undefined}
                            tabIndex={canWriteInvoice && receivableGuideStats.totalPendingAmount ? 0 : undefined}
                        >
                            <div className="sales-revenue-guide-label">待开票合计</div>
                            <div className="sales-revenue-guide-value sales-revenue-money">{formatMoney(receivableGuideStats.totalPendingAmount)}</div>
                            <div className="sales-revenue-guide-detail">
                                {receivableGuideStats.totalPendingAmount ? "点击按当前筛选批量开票" : "暂无待开票应收"}
                            </div>
                        </div>
                        <div className="sales-revenue-guide-cell sales-revenue-guide-cell-gold">
                            <div className="sales-revenue-guide-label">已开待收</div>
                            <div className="sales-revenue-guide-value sales-revenue-money">{formatMoney(invoiceSummary.issuedAmount)}</div>
                            <div className="sales-revenue-guide-detail">已开票等待客户付款</div>
                        </div>
                        <div className="sales-revenue-guide-cell sales-revenue-guide-cell-pink">
                            <div className="sales-revenue-guide-label">已收金额</div>
                            <div className="sales-revenue-guide-value sales-revenue-money">{formatMoney(invoiceSummary.paidAmount)}</div>
                            <div className="sales-revenue-guide-detail">本页发票累计已收</div>
                        </div>
                        <div className="sales-revenue-guide-cell sales-revenue-guide-cell-light">
                            <div className="sales-revenue-guide-label">待收余额</div>
                            <div className="sales-revenue-guide-value sales-revenue-money">{formatMoney(invoiceSummary.outstandingAmount)}</div>
                            <div className="sales-revenue-guide-detail">按发票维度跟踪回款</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="sales-revenue-filter-bar">
                <div className="sales-revenue-filter-item">
                    <label>客户</label>
                    <Select
                        allowClear
                        showSearch
                        loading={customersLoading}
                        value={invoiceFilters.customerId || undefined}
                        placeholder="全部客户"
                        options={customerOptions}
                        onChange={(value) => setInvoiceFilters((prev) => ({ ...prev, customerId: value || "" }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>合同</label>
                    <Select
                        allowClear
                        showSearch
                        loading={receivableContractOptionsLoading}
                        value={invoiceFilters.contractId || undefined}
                        placeholder="全部合同"
                        options={receivableContractSelectOptions}
                        onChange={(value) => setInvoiceFilters((prev) => ({ ...prev, contractId: value || "" }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>发票状态</label>
                    <Select
                        value={invoiceFilters.status}
                        options={INVOICE_STATUS_OPTIONS}
                        onChange={(value) => setInvoiceFilters((prev) => ({ ...prev, status: value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>发票类型</label>
                    <Select
                        value={invoiceFilters.invoiceType}
                        options={INVOICE_TYPE_OPTIONS}
                        onChange={(value) => setInvoiceFilters((prev) => ({ ...prev, invoiceType: value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>航班月份</label>
                    <DatePicker
                        picker="month"
                        className="sales-revenue-full-width"
                        value={invoiceFilters.flightMonth}
                        onChange={(value) => setInvoiceFilters((prev) => ({ ...prev, flightMonth: value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item sales-revenue-filter-item-wide">
                    <label>发票号</label>
                    <Input
                        value={invoiceFilters.invoiceNo}
                        placeholder="如 INV-2026-W15-001"
                        onChange={(event) => setInvoiceFilters((prev) => ({ ...prev, invoiceNo: event.target.value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item sales-revenue-filter-item-wide">
                    <label>CB 订单号</label>
                    <Input
                        value={invoiceFilters.bookingNo}
                        placeholder="如 CB-202604-0002"
                        onChange={(event) => setInvoiceFilters((prev) => ({ ...prev, bookingNo: event.target.value }))}
                    />
                </div>
                <div className="sales-revenue-filter-actions">
                    <Button
                        type="primary"
                        onClick={() => {
                            setInvoicePagination((prev) => ({ ...prev, page: 1 }));
                            loadInvoices();
                        }}
                    >
                        搜索
                    </Button>
                    <Button
                        onClick={() => {
                            setInvoiceFilters({
                                customerId: "",
                                contractId: "",
                                invoiceNo: "",
                                invoiceType: "",
                                status: "",
                                bookingNo: "",
                                flightMonth: null,
                            });
                            setInvoicePagination((prev) => ({ ...prev, page: 1 }));
                        }}
                    >
                        重置
                    </Button>
                </div>
            </div>

            <div className="sales-revenue-card">
                <div className="sales-revenue-card-header">
                    <span>发票列表</span>
                    <span className="sales-revenue-card-meta">
                        共 {invoiceList.total} 张 · 已开 {formatMoney(invoiceSummary.issuedAmount)} · 部分收款 {formatMoney(invoiceSummary.partialAmount)} · 已收 {formatMoney(invoiceSummary.paidAmount)}
                    </span>
                </div>
                <div className="sales-revenue-card-body sales-revenue-table-body">
                    <Table
                        rowKey="id"
                        columns={invoiceColumns}
                        dataSource={invoiceList.items}
                        loading={invoiceLoading}
                        pagination={{
                            current: invoicePagination.page,
                            pageSize: invoicePagination.pageSize,
                            total: invoiceList.total,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => setInvoicePagination({ page, pageSize }),
                        }}
                        scroll={{ x: 1460 }}
                        onRow={(record) => ({
                            onDoubleClick: () => openInvoiceDetail(record),
                        })}
                        locale={{
                            emptyText: (
                                <div className="sales-revenue-empty-state">
                                    <Empty description="暂无发票数据" />
                                    {canWriteInvoice ? (
                                        <Button type="primary" onClick={() => openBatchInvoiceModal("MIXED")}>
                                            按当前应收批量开票
                                        </Button>
                                    ) : null}
                                </div>
                            ),
                        }}
                    />
                </div>
            </div>
        </>
    );

    const renderReceivablesSection = () => (
        <>
            <div className="page-header-compact">
                <h3 className="page-title-compact">应收与收款</h3>
                <div ref={receivableActionsRef} className="page-action-compact">
                    <Space wrap>
                        <Button
                            icon={<QuestionCircleOutlined />}
                            onClick={() => {
                                setReceivableViewKey("flight");
                                setReceivableTourOpen(true);
                            }}
                        >
                            使用教程
                        </Button>
                        <Button icon={<DownloadOutlined />} onClick={handleExportReceivables} disabled={!receivableList.items.length}>
                            导出 CSV
                        </Button>
                        {canWriteReceivable ? (
                            <Button icon={<SyncOutlined />} onClick={openBatchGenerateModal}>
                                基础运价补生成
                            </Button>
                        ) : null}
                        {canGenerateFuel ? (
                            <Button icon={<ArrowRightOutlined />} onClick={openFuelCalcModal}>
                                燃油调整计算
                            </Button>
                        ) : null}
                    </Space>
                </div>
            </div>

            <Alert
                type="info"
                showIcon
                className="sales-revenue-alert"
                message="基础运价应收按月份手动批量生成（本页不会自动出数据）。当前仍只给订单上已写合同的 CB 去程出账；燃油调整需在上月 IATA 油价确认后，通过「燃油调整计算」预览并批量生成。"
            />

            <div ref={receivableFilterRef} className="sales-revenue-filter-bar">
                <div className="sales-revenue-filter-item">
                    <label>客户</label>
                    <Select
                        allowClear
                        showSearch
                        loading={customersLoading}
                        value={receivableFilters.customerId || undefined}
                        placeholder="全部客户"
                        options={customerOptions}
                        onChange={(value) => setReceivableFilters((prev) => ({ ...prev, customerId: value || "" }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>合同</label>
                    <Select
                        allowClear
                        showSearch
                        loading={receivableContractOptionsLoading}
                        value={receivableFilters.contractId || undefined}
                        placeholder="全部合同"
                        options={receivableContractSelectOptions}
                        onChange={(value) => setReceivableFilters((prev) => ({ ...prev, contractId: value || "" }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>航线</label>
                    <Select
                        allowClear
                        value={receivableFilters.route || undefined}
                        placeholder="全部航线"
                        options={receivableRouteOptions}
                        onChange={(value) => setReceivableFilters((prev) => ({ ...prev, route: value || "" }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>状态</label>
                    <Select
                        value={receivableFilters.status}
                        options={RECEIVABLE_STATUS_OPTIONS}
                        onChange={(value) => setReceivableFilters((prev) => ({ ...prev, status: value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>类型</label>
                    <Select
                        value={receivableFilters.type}
                        options={RECEIVABLE_TYPE_OPTIONS}
                        onChange={(value) => setReceivableFilters((prev) => ({ ...prev, type: value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>航班月份</label>
                    <DatePicker
                        picker="month"
                        className="sales-revenue-full-width"
                        value={receivableFilters.flightMonth}
                        onChange={(value) => setReceivableFilters((prev) => ({ ...prev, flightMonth: value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item sales-revenue-filter-item-wide">
                    <label>CB 订单号</label>
                    <Input
                        value={receivableFilters.bookingNo}
                        placeholder="如 CB-202604-0002"
                        onChange={(event) => setReceivableFilters((prev) => ({ ...prev, bookingNo: event.target.value }))}
                    />
                </div>
                <div className="sales-revenue-filter-item">
                    <label>异常筛选</label>
                    <Select
                        value={receivableFilters.hasException}
                        options={RECEIVABLE_EXCEPTION_OPTIONS}
                        onChange={(value) => setReceivableFilters((prev) => ({ ...prev, hasException: value }))}
                    />
                </div>
                <div className="sales-revenue-filter-actions">
                    <Button
                        type="primary"
                        onClick={() => {
                            setReceivablePagination((prev) => ({ ...prev, page: 1 }));
                            loadReceivables();
                        }}
                    >
                        搜索
                    </Button>
                    <Button
                        onClick={() => {
                            setReceivableFilters({
                                customerId: "",
                                contractId: "",
                                route: "",
                                status: "",
                                type: "",
                                bookingNo: "",
                                flightMonth: null,
                                hasException: "",
                            });
                            setReceivablePagination((prev) => ({ ...prev, page: 1 }));
                        }}
                    >
                        重置
                    </Button>
                </div>
            </div>

            <div ref={receivableViewTabsRef} className="sales-revenue-view-tabs">
                <button
                    type="button"
                    className={`sales-revenue-view-tab ${receivableViewKey === "flight" ? "is-active" : ""}`}
                    onClick={() => setReceivableViewKey("flight")}
                >
                    订单维度
                </button>
                <button
                    type="button"
                    className={`sales-revenue-view-tab ${receivableViewKey === "invoice" ? "is-active" : ""}`}
                    onClick={() => setReceivableViewKey("invoice")}
                >
                    发票维度
                </button>
            </div>

            {receivableViewKey === "flight" ? (
                <>
                    <Alert
                        type="info"
                        showIcon
                        className="sales-revenue-alert"
                        message="基础运价应收按 CB 订单生成。当前开放基础运价查询、异常查看、重匹配、按月份补生成，以及燃油调整按月预览生成；收款处理请切换到发票维度。"
                    />

                    <div ref={receivableGuideRef} className="sales-revenue-card sales-revenue-guide-card">
                        <div className="sales-revenue-card-body">
                            <div className="sales-revenue-guide-grid sales-revenue-guide-grid-compact">
                                <div
                                    className={`sales-revenue-guide-cell sales-revenue-guide-cell-blue ${canWriteInvoice && receivableGuideStats.pendingBaseAmount ? "is-clickable" : ""}`}
                                    onClick={canWriteInvoice && receivableGuideStats.pendingBaseAmount ? () => openBatchInvoiceModal("BASE_WEEKLY") : undefined}
                                    role={canWriteInvoice && receivableGuideStats.pendingBaseAmount ? "button" : undefined}
                                    tabIndex={canWriteInvoice && receivableGuideStats.pendingBaseAmount ? 0 : undefined}
                                >
                                    <div className="sales-revenue-guide-label">待开 · 基础运价预付发票</div>
                                    <div className="sales-revenue-guide-value sales-revenue-money">{formatMoney(receivableGuideStats.pendingBaseAmount)}</div>
                                </div>
                                <div
                                    className={`sales-revenue-guide-cell sales-revenue-guide-cell-gold ${canWriteInvoice && receivableGuideStats.pendingFuelAmount ? "is-clickable" : ""}`}
                                    onClick={canWriteInvoice && receivableGuideStats.pendingFuelAmount ? () => openBatchInvoiceModal("FUEL_MONTHLY") : undefined}
                                    role={canWriteInvoice && receivableGuideStats.pendingFuelAmount ? "button" : undefined}
                                    tabIndex={canWriteInvoice && receivableGuideStats.pendingFuelAmount ? 0 : undefined}
                                >
                                    <div className="sales-revenue-guide-label">待开 · 燃油调整发票</div>
                                    <div className="sales-revenue-guide-value sales-revenue-money">{formatMoney(receivableGuideStats.pendingFuelAmount)}</div>
                                </div>
                                <div
                                    className={`sales-revenue-guide-cell sales-revenue-guide-cell-pink ${canWriteInvoice && receivableGuideStats.pendingExtraAmount ? "is-clickable" : ""}`}
                                    onClick={canWriteInvoice && receivableGuideStats.pendingExtraAmount ? () => openBatchInvoiceModal("EXTRA_FEE") : undefined}
                                    role={canWriteInvoice && receivableGuideStats.pendingExtraAmount ? "button" : undefined}
                                    tabIndex={canWriteInvoice && receivableGuideStats.pendingExtraAmount ? 0 : undefined}
                                >
                                    <div className="sales-revenue-guide-label">待开 · 额外费用</div>
                                    <div className="sales-revenue-guide-value sales-revenue-money">{formatMoney(receivableGuideStats.pendingExtraAmount)}</div>
                                </div>
                                <div className="sales-revenue-guide-cell sales-revenue-guide-cell-light">
                                    <div className="sales-revenue-guide-label">合计待开票</div>
                                    <div className="sales-revenue-guide-value sales-revenue-money">{formatMoney(receivableGuideStats.totalPendingAmount)}</div>
                                    {receivableGuideStats.exceptionCount ? (
                                        <div className="sales-revenue-guide-detail sales-revenue-guide-detail-warning">
                                            {receivableGuideStats.exceptionCount} 条异常待处理
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="sales-revenue-card">
                        <div className="sales-revenue-card-header">
                            <span>航班应收总览</span>
                            <Space size={8}>
                                {canWriteReceivable ? (
                                    <Button size="small" icon={<PlusOutlined />} onClick={() => openExtraFeeModal()}>
                                        新增额外费用
                                    </Button>
                                ) : null}
                                <span className="sales-revenue-card-meta">共 {receivableList.total} 笔订单</span>
                            </Space>
                        </div>
                        <div className="sales-revenue-card-body sales-revenue-table-body">
                            <Table
                                rowKey="booking_id"
                                columns={bookingColumns}
                                dataSource={receivableList.items}
                                loading={receivableLoading}
                                onRow={(group) => ({
                                    onClick: () => openBookingDetail(group),
                                    style: { cursor: "pointer" },
                                })}
                                pagination={{
                                    current: receivablePagination.page,
                                    pageSize: receivablePagination.pageSize,
                                    total: receivableList.total,
                                    showSizeChanger: true,
                                    onChange: (page, pageSize) => setReceivablePagination({ page, pageSize }),
                                }}
                                scroll={{ x: 1330 }}
                                locale={{
                                    emptyText: (
                                        <div className="sales-revenue-empty-state">
                                            <Empty
                                                description="还没有应收。请按月手动生成基础运价应收（本页不会自动出数据）。"
                                            />
                                            {canWriteReceivable ? (
                                                <Button type="primary" onClick={openBatchGenerateModal}>
                                                    生成基础运价应收
                                                </Button>
                                            ) : null}
                                        </div>
                                    ),
                                }}
                            />
                        </div>
                    </div>
                </>
            ) : (
                renderReceivableInvoiceView()
            )}
        </>
    );

    const currentSummary = detailContract?.prepayment_summary || {};
    const latestPrepayment = detailContract?.prepayments?.[0];
    const bookingDetail = bookingDetailKey
        ? (receivableList.items || []).find((group) => group.booking_id === bookingDetailKey) || null
        : null;
    const bookingDetailRecord = bookingDetail
        ? {
              charter_booking_id: bookingDetail.booking_id,
              booking_no: bookingDetail.booking_no,
              contract_id: bookingDetail.contract_id,
              currency: bookingDetail.summary?.currency || "USD",
              flight_date_snapshot: bookingDetail.flight_date,
              billing_timezone: "UTC",
          }
        : null;
    const renderContractHeaderFields = () => (
        <div className="sales-revenue-form-grid two-columns">
            <div ref={contractFieldNoRef}>
                <Form.Item
                    label="合同编号"
                    name="contract_no"
                    rules={[{ required: true, message: "请输入合同编号" }]}
                    tooltip={
                        editingContract
                            ? "草稿/生效中可修改（覆盖先飞后补合同的占位编号转正场景）；已终止/已过期不可改"
                            : undefined
                    }
                >
                    <Input
                        placeholder="如 20261001-HKG-XXX"
                        disabled={Boolean(editingContract) && !["DRAFT", "ACTIVE"].includes(editingContract?.status)}
                    />
                </Form.Item>
            </div>
            <Form.Item label="客户" name="customer_id" rules={[{ required: true, message: "请选择客户" }]}>
                <Select
                    showSearch
                    optionFilterProp="label"
                    loading={customersLoading}
                    options={customerOptions}
                    placeholder="请选择客户"
                    disabled={Boolean(editingContract)}
                />
            </Form.Item>
            <div ref={contractFieldKindRef}>
                <Form.Item
                    label="合同类型"
                    name="contract_kind"
                    rules={[{ required: true, message: "请选择合同类型" }]}
                    tooltip="阶梯按油价档位。按段油耗按实际油耗。一班一议燃油手填。时段包机整段只收一笔包干价。类型创建后不可更改。"
                >
                    <Select
                        options={CONTRACT_KIND_OPTIONS}
                        disabled={Boolean(editingContract)}
                        placeholder="请选择合同类型"
                        onChange={(kind) => {
                            if (!editingContract && kind !== "ACMI") {
                                contractForm.setFieldValue("status", "DRAFT");
                            }
                        }}
                    />
                </Form.Item>
            </div>
            <div ref={contractFieldDateRef}>
                <Form.Item
                    label="生效日期"
                    name="start_date"
                dependencies={["contract_kind"]}
                tooltip={contractKindDraft === "AD_HOC" ? "一班一议合同可留空，表示不限起始" : undefined}
                rules={[
                    ({ getFieldValue }) => ({
                        required: getFieldValue("contract_kind") !== "AD_HOC",
                        message: "请选择生效日期",
                    }),
                ]}
            >
                <DatePicker
                    className="sales-revenue-full-width"
                    format="YYYY-MM-DD"
                    allowClear={contractKindDraft === "AD_HOC"}
                    placeholder={contractKindDraft === "AD_HOC" ? "可留空 = 不限起始" : "请选择日期"}
                />
            </Form.Item>
            </div>
            <Form.Item
                label="失效日期"
                name="end_date"
                dependencies={["contract_kind"]}
                tooltip={contractKindDraft === "AD_HOC" ? "一班一议合同可留空，表示不限结束" : undefined}
                rules={[
                    ({ getFieldValue }) => ({
                        required: getFieldValue("contract_kind") !== "AD_HOC",
                        message: "请选择失效日期",
                    }),
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            const start = getFieldValue("start_date");
                            if (value && start && dayjs(value).isBefore(dayjs(start), "day")) {
                                return Promise.reject(new Error("失效日期不能早于生效日期"));
                            }
                            return Promise.resolve();
                        },
                    }),
                ]}
            >
                <DatePicker
                    className="sales-revenue-full-width"
                    format="YYYY-MM-DD"
                    allowClear={contractKindDraft === "AD_HOC"}
                    placeholder={contractKindDraft === "AD_HOC" ? "可留空 = 不限结束" : "请选择日期"}
                />
            </Form.Item>
            <Form.Item label="币种" name="currency">
                <Select options={currencyOptions} />
            </Form.Item>
            {contractKindDraft === "ACMI" ? (
                <>
                    <Form.Item label="机号" name="acmi_registration" rules={[{ required: true, message: "请选择机号" }]} extra="和航班监控同一份飞机名单，可以搜索注册号。">
                        <AircraftSelector allowClear={false} className="sales-revenue-full-width" displayMode="simple" placeholder="选择飞机注册号" valueType="registration" />
                    </Form.Item>
                    <Form.Item label="包干价" name="acmi_lump_sum" rules={[{ required: true, message: "请填写大于 0 的包干价" }]} extra="整段只收这一笔，不按航线、班次或小时。">
                        <InputNumber className="sales-revenue-full-width" min={0.01} precision={2} placeholder="800000" />
                    </Form.Item>
                </>
            ) : null}
            {contractKindDraft !== "AD_HOC" && contractKindDraft !== "PER_LEG_BURN" && contractKindDraft !== "ACMI" ? (
                <div ref={contractFieldFuelRef}>
                    <Form.Item
                        label="基准油价 ($/MT)"
                        name="base_fuel_price_per_mt"
                        extra="阶梯合同选填。全合同共用这一个数，用来算油价偏差落在哪一档。"
                    >
                        <InputNumber className="sales-revenue-full-width" min={0} precision={2} placeholder="764.89" />
                    </Form.Item>
                </div>
            ) : null}
            {contractKindDraft === "ACMI" ? null : (
            <Form.Item label="频次" name="frequency_text">
                <Input placeholder="如 每周 4 班" />
            </Form.Item>
            )}
            {contractKindDraft === "ACMI" ? null : (
            <Form.Item label="机型概述" name="aircraft_text" extra="可多选。只是概述，不算价，也不要求和航线价格表一致。">
                <Select
                    allowClear
                    mode="tags"
                    options={AIRCRAFT_OPTIONS}
                    placeholder="可多选，如 B747-400F、B777F"
                    tokenSeparators={[",", "、", "，", "/"]}
                />
            </Form.Item>
            )}
            <Form.Item
                label="状态"
                name="status"
                extra={
                    contractKindDraft === "ACMI"
                        ? editingContract
                            ? "生效中不能退回草稿。已经出了包干之后，不能再改机号、日期和包干价。"
                            : "时段包机不需要航线价，新建时可以直接选生效。"
                        : editingContract
                          ? "生效中不能退回草稿。改成生效前必须已有启用的航线价格。同一客户的多份合同可以落在同一时段。"
                          : "新建后先是草稿。配好航线价格，再从列表启用。"
                }
            >
                <Select
                    options={
                        editingContract
                            ? contractStatusEditOptions(editingContract.status)
                            : contractKindDraft === "ACMI"
                              ? [
                                    { label: "草稿", value: "DRAFT" },
                                    { label: "生效中", value: "ACTIVE" },
                                ]
                              : [{ label: "草稿", value: "DRAFT" }]
                    }
                    disabled={Boolean(editingContract) ? editingContract.status === "TERMINATED" : contractKindDraft !== "ACMI"}
                />
            </Form.Item>
            <Form.Item label="备注" name="remark" className="sales-revenue-span-2">
                <Input.TextArea rows={4} placeholder="合同附加说明..." />
            </Form.Item>
        </div>
    );

    const openedContract = detailContract?.id === routeContractId ? detailContract : null;
    const contractDetailFailed = contractDetailError?.contractId === routeContractId;

    const currentSectionContent = (
        <>
            {activeSection === "routes" ? renderRoutesSection() : null}
            {activeSection === "contracts" ? renderContractsSection() : null}
            {activeSection === "receivables" ? renderReceivablesSection() : null}
        </>
    );

    return (
        <div className="sales-revenue-page">
            {keepContractList ? <div hidden={Boolean(routeContractId)}>{currentSectionContent}</div> : null}

            <Modal
                open={routeModalOpen}
                title={editingRoute ? "编辑去程航线" : "新增去程航线"}
                onCancel={() => setRouteModalOpen(false)}
                onOk={handleRouteSubmit}
                confirmLoading={routeSubmitting}
                okText={editingRoute ? "保存" : "创建"}
                cancelText="取消"
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    className="sales-revenue-modal-alert"
                    message="这里只录去程航线，始发站和目的地以合同 O-D 为准，经停站后续可继续补充。"
                />
                <Form form={routeForm} layout="vertical">
                    <div className="sales-revenue-form-grid two-columns">
                        <Form.Item label="航线代码" name="route_code">
                            <Input value={routeCodePreview} placeholder="根据始发站和目的地自动生成" disabled />
                        </Form.Item>
                        <Form.Item label="航线名称" name="name" rules={[{ required: true, message: "请输入航线名称" }]}>
                            <Input placeholder="如：香港-列日" />
                        </Form.Item>
                        <Form.Item label="始发站" name="origin_airport" rules={[{ required: true, message: "请输入始发站" }]}>
                            <Input
                                placeholder="如 HKG"
                                maxLength={12}
                                disabled={Boolean(editingRoute)}
                                onChange={(event) => routeForm.setFieldValue("origin_airport", normalizeAirport(event.target.value))}
                            />
                        </Form.Item>
                        <Form.Item label="目的地" name="destination_airport" rules={[{ required: true, message: "请输入目的地" }]}>
                            <Input
                                placeholder="如 LGG"
                                maxLength={12}
                                disabled={Boolean(editingRoute)}
                                onChange={(event) => routeForm.setFieldValue("destination_airport", normalizeAirport(event.target.value))}
                            />
                        </Form.Item>
                        <Form.Item label="常见经停" name="historical_stops" className="sales-revenue-span-2">
                            <Select
                                mode="tags"
                                tokenSeparators={[",", " "]}
                                placeholder="可选，如 HSA、NQZ、DWC"
                                onChange={(values) => routeForm.setFieldValue("historical_stops", normalizeStops(values))}
                            />
                        </Form.Item>
                        <Form.Item label="状态" name="status">
                            <Select options={ROUTE_STATUS_OPTIONS.filter((item) => item.value)} />
                        </Form.Item>
                        <Form.Item label="来源" name="source_type">
                            <Select
                                disabled
                                options={Object.entries(ROUTE_SOURCE_TEXT).map(([value, label]) => ({
                                    value,
                                    label,
                                }))}
                            />
                        </Form.Item>
                    </div>
                </Form>
            </Modal>

            <Modal
                open={contractModalOpen}
                title={
                    editingContract ? (
                        "编辑销售合同"
                    ) : (
                        <Space>
                            新建销售合同
                            <Button type="text" size="small" icon={<QuestionCircleOutlined />} onClick={() => setContractModalGuideOpen(true)}>
                                填写引导
                            </Button>
                        </Space>
                    )
                }
                onCancel={() => {
                    setContractModalOpen(false);
                    setContractModalAutoGuide(false);
                }}
                onOk={handleContractSubmit}
                confirmLoading={contractSubmitting}
                okText={editingContract ? "保存" : "创建"}
                cancelText="取消"
                afterOpenChange={(opened) => {
                    if (opened && contractModalAutoGuide && !editingContract) {
                        setContractModalGuideOpen(true);
                    }
                    if (!opened) {
                        setContractModalGuideOpen(false);
                        setContractModalAutoGuide(false);
                    }
                }}
                destroyOnClose
            >
                <Form form={contractForm} layout="vertical">
                    {renderContractHeaderFields()}
                </Form>

                <Tour
                    open={contractModalGuideOpen}
                    steps={contractModalGuideSteps}
                    onClose={() => setContractModalGuideOpen(false)}
                    onFinish={() => setContractModalGuideOpen(false)}
                    zIndex={1100}
                />
            </Modal>

            {routeContractId ? (
            <div className="sales-revenue-contract-page">
                <Form component={false} form={contractForm}>
                <div className="sales-revenue-contract-hero">
                    <div>
                        <Button type="link" className="sales-revenue-back-link" onClick={() => navigate("/sales/charter-contracts")}>
                            返回列表
                        </Button>
                        <div className="sales-revenue-contract-hero-title">
                            <h2>
                                {openedContract?.contract_no || (contractDetailFailed ? "合同打不开" : "正在打开合同")}
                            </h2>
                            {openedContract ? (
                                <Space size={4} wrap>
                                    <SectionTag
                                        tone={
                                            openedContract.contract_kind === "PER_LEG_BURN"
                                                ? "blue"
                                                : openedContract.contract_kind === "AD_HOC"
                                                  ? "gold"
                                                  : "gray"
                                        }
                                    >
                                        {CONTRACT_KIND_TEXT[openedContract.contract_kind] || "阶梯合同"}
                                    </SectionTag>
                                    {isContractPastEndDate(openedContract) ? <SectionTag tone="gold">已过失效日</SectionTag> : null}
                                </Space>
                            ) : null}
                        </div>
                        {openedContract ? (
                            <div className="sales-revenue-contract-hero-meta">
                                {openedContract.customer_name || "未填客户"}
                                {" · "}
                                {!openedContract.start_date && !openedContract.end_date
                                    ? "永久有效"
                                    : formatRange(openedContract.start_date, openedContract.end_date)}
                                {" · "}
                                {openedContract.currency}
                            </div>
                        ) : null}
                    </div>
                    {canWriteContract && openedContract ? (
                        <div className="sales-revenue-contract-hero-actions">
                            {contractHeaderEditing ? (
                                <Space>
                                    <Button
                                        onClick={() => {
                                            fillContractForm(openedContract);
                                            setContractHeaderEditing(false);
                                        }}
                                    >
                                        取消
                                    </Button>
                                    <Button loading={contractSubmitting} onClick={handleContractSubmit} type="primary">
                                        保存
                                    </Button>
                                </Space>
                            ) : (
                                <Button
                                    onClick={() => {
                                        fillContractForm(openedContract);
                                        setContractHeaderEditing(true);
                                    }}
                                    type="primary"
                                >
                                    编辑
                                </Button>
                            )}
                        </div>
                    ) : null}
                </div>
                {openedContract ? (
                    <div className="sales-revenue-detail">
                        {contractHeaderEditing ? (
                            <Alert
                                showIcon
                                style={{ marginBottom: 16 }}
                                type="info"
                                message={
                                    openedContract.contract_kind === "ACMI"
                                        ? "生效中不能退回草稿。已经出了包干之后，不能再改机号、日期和包干价。"
                                        : "生效中不能退回草稿。改成生效前必须已有启用的航线价格。同一客户的多份合同可以落在同一时段。"
                                }
                            />
                        ) : null}
                        <Descriptions
                            className="sales-revenue-desc"
                            bordered
                            size="small"
                            column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
                            labelStyle={{ width: 96, color: "var(--sales-text-secondary)" }}
                            style={{ marginBottom: 16 }}
                            items={[
                                {
                                    key: "contract_no",
                                    label: "合同编号",
                                    children: contractHeaderEditing ? (
                                        <Form.Item
                                            className="sales-revenue-inline-field"
                                            name="contract_no"
                                            rules={[{ required: true, message: "请输入合同编号" }]}
                                        >
                                            <Input
                                                disabled={!["DRAFT", "ACTIVE"].includes(detailContract.status)}
                                                placeholder="如 20261001-HKG-XXX"
                                            />
                                        </Form.Item>
                                    ) : (
                                        detailContract.contract_no
                                    ),
                                },
                                { key: "customer", label: "客户", children: detailContract.customer_name || "—" },
                                {
                                    key: "status",
                                    label: "状态",
                                    children: contractHeaderEditing ? (
                                        <Form.Item className="sales-revenue-inline-field" name="status">
                                            <Select
                                                disabled={detailContract.status === "TERMINATED"}
                                                options={contractStatusEditOptions(detailContract.status)}
                                            />
                                        </Form.Item>
                                    ) : (
                                        <SectionTag tone={getToneClass("contract-status", detailContract.status)}>
                                            {CONTRACT_STATUS_TEXT[detailContract.status] || detailContract.status}
                                        </SectionTag>
                                    ),
                                },
                                {
                                    key: "period",
                                    label: "有效期",
                                    children: contractHeaderEditing ? (
                                        <div className="sales-revenue-inline-dates">
                                            <Form.Item
                                                className="sales-revenue-inline-field"
                                                dependencies={["contract_kind"]}
                                                name="start_date"
                                                rules={[
                                                    ({ getFieldValue }) => ({
                                                        required: getFieldValue("contract_kind") !== "AD_HOC",
                                                        message: "请选择生效日期",
                                                    }),
                                                ]}
                                            >
                                                <DatePicker
                                                    allowClear={detailContract.contract_kind === "AD_HOC"}
                                                    format="YYYY-MM-DD"
                                                    placeholder={detailContract.contract_kind === "AD_HOC" ? "不限起始" : "生效"}
                                                />
                                            </Form.Item>
                                            <span>至</span>
                                            <Form.Item
                                                className="sales-revenue-inline-field"
                                                dependencies={["start_date", "contract_kind"]}
                                                name="end_date"
                                                rules={[
                                                    ({ getFieldValue }) => ({
                                                        required: getFieldValue("contract_kind") !== "AD_HOC",
                                                        message: "请选择失效日期",
                                                    }),
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            const start = getFieldValue("start_date");
                                                            if (value && start && dayjs(value).isBefore(dayjs(start), "day")) {
                                                                return Promise.reject(new Error("失效日期不能早于生效日期"));
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <DatePicker
                                                    allowClear={detailContract.contract_kind === "AD_HOC"}
                                                    format="YYYY-MM-DD"
                                                    placeholder={detailContract.contract_kind === "AD_HOC" ? "不限结束" : "失效"}
                                                />
                                            </Form.Item>
                                        </div>
                                    ) : !detailContract.start_date && !detailContract.end_date ? (
                                        "永久有效（未设有效期）"
                                    ) : (
                                        formatRange(detailContract.start_date, detailContract.end_date)
                                    ),
                                },
                                {
                                    key: "currency",
                                    label: "币种",
                                    children: contractHeaderEditing ? (
                                        <Form.Item className="sales-revenue-inline-field" name="currency">
                                            <Select options={currencyOptions} />
                                        </Form.Item>
                                    ) : (
                                        detailContract.currency
                                    ),
                                },
                                ...(detailContract.contract_kind === "ACMI"
                                    ? [
                                          {
                                              key: "acmi_reg",
                                              label: "机号",
                                              children: contractHeaderEditing ? (
                                                  <Form.Item
                                                      className="sales-revenue-inline-field"
                                                      name="acmi_registration"
                                                      rules={[{ required: true, message: "请选择机号" }]}
                                                  >
                                                      <AircraftSelector
                                                          allowClear={false}
                                                          className="sales-revenue-full-width"
                                                          displayMode="simple"
                                                          placeholder="选择飞机注册号"
                                                          valueType="registration"
                                                      />
                                                  </Form.Item>
                                              ) : (
                                                  detailContract.acmi_registration || "—"
                                              ),
                                          },
                                          {
                                              key: "acmi_sum",
                                              label: "包干价",
                                              children: contractHeaderEditing ? (
                                                  <Form.Item
                                                      className="sales-revenue-inline-field"
                                                      name="acmi_lump_sum"
                                                      rules={[{ required: true, message: "请填写大于 0 的包干价" }]}
                                                  >
                                                      <InputNumber className="sales-revenue-full-width" min={0.01} precision={2} placeholder="800000" />
                                                  </Form.Item>
                                              ) : (
                                                  `${formatMoney(detailContract.acmi_lump_sum)} ${detailContract.currency}`
                                              ),
                                          },
                                      ]
                                    : []),
                                ...(detailContract.contract_kind === "AD_HOC" ||
                                detailContract.contract_kind === "ACMI" ||
                                (detailContract.contract_kind === "PER_LEG_BURN" && detailContract.base_fuel_price_per_mt == null)
                                    ? []
                                    : [
                                          {
                                              key: "base_fuel",
                                              label: "基准油价",
                                              children: contractHeaderEditing ? (
                                                  <Form.Item className="sales-revenue-inline-field" name="base_fuel_price_per_mt">
                                                      <InputNumber className="sales-revenue-full-width" min={0} precision={2} placeholder="764.89" />
                                                  </Form.Item>
                                              ) : (
                                                  <span className="sales-revenue-money">
                                                      {detailContract.base_fuel_price_per_mt == null
                                                          ? "—"
                                                          : `${formatMoney(detailContract.base_fuel_price_per_mt)} ${detailContract.currency}/MT`}
                                                  </span>
                                              ),
                                          },
                                      ]),
                                {
                                    key: "frequency",
                                    label: "频次",
                                    children:
                                        detailContract.contract_kind === "ACMI"
                                            ? "—"
                                            : contractHeaderEditing
                                              ? (
                                                  <Form.Item className="sales-revenue-inline-field" name="frequency_text">
                                                      <Input placeholder="如 每周 4 班" />
                                                  </Form.Item>
                                              )
                                              : detailContract.frequency_text || "—",
                                },
                                {
                                    key: "aircraft",
                                    label: "机型概述",
                                    children:
                                        detailContract.contract_kind === "ACMI"
                                            ? "—"
                                            : contractHeaderEditing
                                              ? (
                                                  <Form.Item className="sales-revenue-inline-field" name="aircraft_text">
                                                      <Select
                                                          allowClear
                                                          mode="tags"
                                                          options={AIRCRAFT_OPTIONS}
                                                          placeholder="可多选，如 B747-400F、B777F"
                                                          tokenSeparators={[",", "、", "，", "/"]}
                                                      />
                                                  </Form.Item>
                                              )
                                              : detailContract.aircraft_text || "—",
                                },
                                {
                                    key: "remark",
                                    label: "备注",
                                    children: contractHeaderEditing ? (
                                        <Form.Item className="sales-revenue-inline-field" name="remark">
                                            <Input.TextArea rows={2} placeholder="合同附加说明..." />
                                        </Form.Item>
                                    ) : (
                                        detailContract.remark || "—"
                                    ),
                                },
                                { key: "updated", label: "更新时间", children: formatDateTime(detailContract.updated_at) },
                            ]}
                        />

                        <div className="sales-revenue-prepayment-summary">
                            <div className="sales-revenue-prepayment-summary-main">
                                <div className="sales-revenue-prepayment-title">合同预付款</div>
                                <div className="sales-revenue-prepayment-subtitle">
                                    {latestPrepayment ? `最近一次收款日期：${formatDate(latestPrepayment.received_date)}` : "尚未录入预付款"}
                                </div>
                            </div>
                            <div className="sales-revenue-prepayment-stats">
                                <div className="sales-revenue-prepayment-stat">
                                    <div className="sales-revenue-prepayment-stat-label">预付总额</div>
                                    <div className="sales-revenue-prepayment-stat-value sales-revenue-money">
                                        {formatMoney(currentSummary.total_original_amount)} {detailContract.currency}
                                    </div>
                                </div>
                                <div className="sales-revenue-prepayment-stat">
                                    <div className="sales-revenue-prepayment-stat-label">已抵扣</div>
                                    <div className="sales-revenue-prepayment-stat-value sales-revenue-money">
                                        {formatMoney(currentSummary.total_used_amount)} {detailContract.currency}
                                    </div>
                                </div>
                                <div className="sales-revenue-prepayment-stat">
                                    <div className="sales-revenue-prepayment-stat-label">剩余可用</div>
                                    <div className="sales-revenue-prepayment-stat-value sales-revenue-money sales-revenue-money-success">
                                        {formatMoney(currentSummary.total_remaining_amount)} {detailContract.currency}
                                    </div>
                                </div>
                            </div>
                            {canWriteContract ? (
                                <Button type="primary" size="small" onClick={openCreatePrepaymentModal}>
                                    录入预付款
                                </Button>
                            ) : null}
                        </div>

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>预付款流水</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    columns={prepaymentColumns}
                                    dataSource={detailContract.prepayments || []}
                                    locale={{ emptyText: "暂无预付款记录" }}
                                />
                            </div>
                        </div>

                        <Tabs
                            activeKey={detailTabKey}
                            onChange={handleDetailTabChange}
                            items={[
                                {
                                    key: "prices",
                                    label: detailContract.contract_kind === "ACMI" ? "未并进来的航班" : "合同航线",
                                    children: detailContract.contract_kind === "ACMI" ? (
                                        <>
                                            <Alert
                                                type="info"
                                                showIcon
                                                className="sales-revenue-modal-alert"
                                                message="这段日子按包干价收一笔，不按航线计价，这一版不算燃油。"
                                                description={detailContract.acmi_pending_message || "下面只列出还没挂到这份合同上的航班，不会自动改挂。"}
                                            />
                                            <Table
                                                rowKey="flight_operation_id"
                                                size="small"
                                                pagination={false}
                                                dataSource={detailContract.acmi_pending_flights || []}
                                                locale={{ emptyText: detailContract.acmi_incomplete_count ? "名单里没有可改挂的航班" : "已经都在挂着这份合同的订单上" }}
                                                columns={[
                                                    { title: "航班", dataIndex: "flight_number" },
                                                    { title: "航段", render: (_, row) => `${row.departure_airport || ""}-${row.arrival_airport || ""}` },
                                                    { title: "机号", dataIndex: "aircraft_reg" },
                                                    { title: "订单", dataIndex: "booking_no", render: (value) => value || "—" },
                                                ]}
                                            />
                                            <div className="sales-revenue-subsection-header">
                                                <span>顶班</span>
                                            </div>
                                            <Table
                                                rowKey="id"
                                                size="small"
                                                pagination={false}
                                                dataSource={detailContract.acmi_substitutes || []}
                                                locale={{ emptyText: "还没有顶班" }}
                                                columns={[
                                                    { title: "日期", dataIndex: "comparison_date" },
                                                    { title: "机号", dataIndex: "registration" },
                                                    {
                                                        title: "",
                                                        render: (_, row) =>
                                                            canWriteContract ? (
                                                                <Button size="small" onClick={() => handleDeleteAcmiSubstitute(row.id)}>
                                                                    删除
                                                                </Button>
                                                            ) : null,
                                                    },
                                                ]}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <div className="sales-revenue-subsection-header">
                                                <span>输入新航线时会自动同步到航线池</span>
                                                {canWriteContract ? (
                                                    <Button type="primary" size="small" onClick={openCreateRoutePriceModal}>
                                                        添加合同航线
                                                    </Button>
                                                ) : null}
                                            </div>
                                            <Table
                                                rowKey="id"
                                                size="small"
                                                pagination={false}
                                                columns={priceColumns}
                                                dataSource={detailContract.route_prices || []}
                                                rowClassName={(record) => (record.status === "INACTIVE" ? "sales-revenue-row-inactive" : "")}
                                                locale={{ emptyText: "暂无合同航线" }}
                                            />
                                        </>
                                    ),
                                },
                                {
                                    key: "fuel",
                                    label: detailContract.contract_kind === "PER_LEG_BURN" || detailContract.contract_kind === "AD_HOC" || detailContract.contract_kind === "ACMI" ? "燃油说明" : "燃油阶梯表",
                                    children:
                                        detailContract.contract_kind === "ACMI" ? (
                                            <Alert type="info" showIcon className="sales-revenue-modal-alert" message="这一版不算燃油" description="时段包机不配燃油阶梯，批量算燃油时会跳过挂着这份合同的订单。" />
                                        ) : detailContract.contract_kind === "AD_HOC" ? (
                                            <Alert
                                                type="info"
                                                showIcon
                                                className="sales-revenue-modal-alert"
                                                message="本合同不算燃油"
                                                description="若这班谈了燃油，到订单详情手填。不要在这里配燃油阶梯。"
                                            />
                                        ) : detailContract.contract_kind === "PER_LEG_BURN" ? (
                                        <Alert
                                            type="info"
                                            showIcon
                                            className="sales-revenue-modal-alert"
                                            message="本合同燃油按段计算（不配阶梯）"
                                            description={
                                                <div>
                                                    <p style={{ margin: "4px 0" }}>
                                                        整单燃油按段加总。每一段用该起飞机场当月的市场价，基准价也按机场，不要求在合同头填一个全合同共用的数。
                                                    </p>
                                                    <p style={{ margin: "4px 0", color: "var(--sales-text-secondary)" }}>
                                                        {detailContract.base_fuel_price_per_mt == null
                                                            ? "合同头没有基准油价。各机场自己的基准价还没有单独的录入位置，所以现在还生成不了燃油应收。"
                                                            : `合同头上留着基准油价 ${formatMoney(detailContract.base_fuel_price_per_mt)} ${detailContract.currency}/MT。当前算账仍用这一个数去减每一段的市场价。`}
                                                        在应收页打开订单详情的「航段油耗」页签挑段、填油耗。
                                                    </p>
                                                </div>
                                            }
                                        />
                                    ) : (
                                        <>
                                            <Alert
                                                type="info"
                                                showIcon
                                                className="sales-revenue-modal-alert"
                                                message={`匹配逻辑：diff = 上月 $/MT 均值 - 基准油价（${detailContract.base_fuel_price_per_mt == null ? "—" : formatMoney(detailContract.base_fuel_price_per_mt)}）。`}
                                            />
                                            <div className="sales-revenue-subsection-header">
                                                <span>油价高于基准时，根据 diff 落入区间确定单班燃油附加费。</span>
                                                {canWriteContract ? (
                                                    <Button type="primary" size="small" onClick={openCreateFuelTierModal}>
                                                        添加燃油阶梯
                                                    </Button>
                                                ) : null}
                                            </div>
                                            <Table
                                                rowKey="id"
                                                size="small"
                                                pagination={false}
                                                columns={fuelTierColumns}
                                                dataSource={detailContract.fuel_surcharge_tiers || []}
                                                locale={{ emptyText: "暂无燃油阶梯" }}
                                            />
                                        </>
                                    ),
                                },
                                {
                                    key: "bookings",
                                    label: "绑定订单",
                                    children: (
                                        <>
                                            <div className="sales-revenue-subsection-header">
                                                <span>
                                                    {detailContract.contract_kind === "AD_HOC"
                                                        ? "绑到这份合同的 CB 订单。基础运价按航线价生成；燃油若谈了，点「手填燃油」。"
                                                        : "绑到这份合同的 CB 订单，及其基础运价应收状态。"}
                                                </span>
                                                <Button
                                                    size="small"
                                                    icon={<ReloadOutlined />}
                                                    loading={contractBookingsLoading}
                                                    onClick={() =>
                                                        loadContractBookings(
                                                            detailContract.id,
                                                            contractBookingsQuery.page,
                                                            contractBookingsQuery.pageSize,
                                                        )
                                                    }
                                                >
                                                    刷新
                                                </Button>
                                            </div>
                                            <Table
                                                rowKey="id"
                                                size="small"
                                                loading={contractBookingsLoading}
                                                pagination={{
                                                    current: contractBookingsQuery.page,
                                                    pageSize: contractBookingsQuery.pageSize,
                                                    total: contractBookingsQuery.total,
                                                    showSizeChanger: true,
                                                    onChange: (page, pageSize) => loadContractBookings(detailContract.id, page, pageSize),
                                                }}
                                                columns={contractBookingColumns}
                                                dataSource={contractBookings}
                                                locale={{ emptyText: "暂无绑定到本合同的订单" }}
                                            />
                                        </>
                                    ),
                                },
                            ]}
                        />
                    </div>
                ) : contractDetailFailed && !detailLoading ? (
                    <div className="sales-revenue-detail-error">
                        <Alert
                            showIcon
                            type="error"
                            message="这份合同打不开"
                            description={
                                contractDetailError.message && contractDetailError.message !== "获取合同详情失败"
                                    ? contractDetailError.message
                                    : "可能已经删除，或当前账号看不到它。"
                            }
                        />
                        <Button onClick={() => loadContractDetail(routeContractId)}>重试</Button>
                    </div>
                ) : (
                    <div className="sales-revenue-loading">
                        <Spin />
                    </div>
                )}
                </Form>
            </div>
            ) : null}

            <Modal
                open={Boolean(adHocPanelBooking)}
                title={adHocPanelBooking ? `一班一议手填燃油：${adHocPanelBooking.booking_no || ""}` : "一班一议手填燃油"}
                onCancel={() => setAdHocPanelBooking(null)}
                footer={null}
                width={760}
                destroyOnClose
            >
                {adHocPanelBooking ? (
                    <AdHocAmountPanel
                        bookingId={adHocPanelBooking.charter_booking_id || adHocPanelBooking.id}
                        canWrite={canWriteReceivable}
                        currencyOptions={currencyOptions}
                        messageApi={messageApi}
                        modalApi={modalApi}
                        onGenerated={async () => {
                            await Promise.all([
                                loadReceivables(),
                                detailContract?.id ? loadContractBookings(detailContract.id) : Promise.resolve(),
                            ]);
                        }}
                    />
                ) : null}
            </Modal>

            <Modal
                open={routePriceModalOpen}
                title={editingRoutePrice ? "编辑合同航线" : "添加合同航线"}
                onCancel={() => setRoutePriceModalOpen(false)}
                onOk={handleRoutePriceSubmit}
                confirmLoading={routePriceSubmitting}
                okText={editingRoutePrice ? "保存" : "添加"}
                cancelText="取消"
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    className="sales-revenue-modal-alert"
                    message="优先从航线池选择已有航线；若是新航线，可直接在下方手动输入，保存后会自动同步到航线池。"
                />
                <Form form={routePriceForm} layout="vertical">
                    <div className="sales-revenue-form-section">
                        <div className="sales-revenue-form-section-title">航线</div>
                        <div className="sales-revenue-form-grid two-columns">
                            {!editingRoutePrice ? (
                                <Form.Item label="选择航线" className="sales-revenue-span-2">
                                    <Select
                                        showSearch
                                        allowClear
                                        filterOption={false}
                                        loading={routePoolOptionsLoading}
                                        options={routePoolOptions}
                                        placeholder="输入机场或航线代码搜索；搜不到可在下方手填"
                                        onSearch={handleRoutePoolSearch}
                                        onChange={handleRoutePoolSelect}
                                    />
                                </Form.Item>
                            ) : null}
                            <Form.Item label="始发站" name="origin_airport" rules={[{ required: true, message: "请输入始发站" }]}>
                                <Input
                                    placeholder="如 HKG"
                                    maxLength={12}
                                    disabled={Boolean(editingRoutePrice)}
                                    onChange={(event) => routePriceForm.setFieldValue("origin_airport", normalizeAirport(event.target.value))}
                                />
                            </Form.Item>
                            <Form.Item label="目的地" name="destination_airport" rules={[{ required: true, message: "请输入目的地" }]}>
                                <Input
                                    placeholder="如 MXP"
                                    maxLength={12}
                                    disabled={Boolean(editingRoutePrice)}
                                    onChange={(event) => routePriceForm.setFieldValue("destination_airport", normalizeAirport(event.target.value))}
                                />
                            </Form.Item>
                            <Form.Item label="机型" name="aircraft_type">
                                <Select allowClear options={AIRCRAFT_OPTIONS} placeholder="不限机型（默认价）" />
                            </Form.Item>
                        </div>
                    </div>
                    <div className="sales-revenue-form-section">
                        <div className="sales-revenue-form-section-title">价格</div>
                        <Form.List
                            name="price_rows"
                            rules={[
                                {
                                    validator: async (_, rows) => {
                                        if (!rows?.length) {
                                            throw new Error("至少要有一档价");
                                        }
                                    },
                                },
                            ]}
                        >
                            {(fields, { add, remove }, { errors }) => {
                                const used = new Set((routePriceForm.getFieldValue("price_rows") || []).map((item) => item?.kind));
                                const remaining = ROUTE_PRICE_KINDS.filter((item) => !used.has(item.value));
                                return (
                                    <div className="sales-revenue-price-list">
                                        {fields.map((field) => {
                                            const kind = routePriceForm.getFieldValue(["price_rows", field.name, "kind"]);
                                            return (
                                                <div className="sales-revenue-price-card" key={field.key}>
                                                    <Form.Item name={[field.name, "kind"]} noStyle>
                                                        <Input type="hidden" />
                                                    </Form.Item>
                                                    <div className="sales-revenue-price-kind">{routePriceKindLabel(kind)}</div>
                                                    <div className="sales-revenue-price-row">
                                                        <Form.Item
                                                            name={[field.name, "unit_price"]}
                                                            rules={[{ required: true, message: "请输入单价" }]}
                                                        >
                                                            <InputNumber className="sales-revenue-full-width" min={0} precision={2} placeholder="单价" />
                                                        </Form.Item>
                                                        <Button type="link" danger onClick={() => remove(field.name)}>
                                                            移除
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {fields.length < ROUTE_PRICE_KINDS.length ? (
                                            <Dropdown
                                                menu={{
                                                    items: remaining.map((item) => ({ key: item.value, label: item.label })),
                                                    onClick: ({ key }) => add({ kind: key, unit_price: null }),
                                                }}
                                            >
                                                <Button icon={<PlusOutlined />}>添加价格</Button>
                                            </Dropdown>
                                        ) : null}
                                        <Form.ErrorList errors={errors} />
                                    </div>
                                );
                            }}
                        </Form.List>
                        <div className="sales-revenue-form-grid two-columns">
                            <Form.Item label="币种" name="currency">
                                <Select options={currencyOptions} />
                            </Form.Item>
                            <div className="sales-revenue-span-2 ant-form-item-extra">同一种只能加一次。没有直飞或经停价时，用不限飞法。</div>
                            <Form.Item noStyle shouldUpdate>
                                {() => {
                                    const rawCurrency = routePriceForm.getFieldValue("currency");
                                    const rawContractCurrency = detailContract?.currency;
                                    if (!rawCurrency || !rawContractCurrency) {
                                        return null;
                                    }
                                    const currency = normalizeCurrency(rawCurrency);
                                    const contractCurrency = normalizeCurrency(rawContractCurrency);
                                    if (currency === contractCurrency) {
                                        return null;
                                    }
                                    return (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            className="sales-revenue-span-2"
                                            message={`这一档用 ${currency}，合同是 ${contractCurrency}。金额按航线币种，不会改写成合同币种。`}
                                        />
                                    );
                                }}
                            </Form.Item>
                        </div>
                    </div>
                    <div className="sales-revenue-form-section">
                        <div className="sales-revenue-form-section-title">有效期</div>
                        <div className="sales-revenue-form-grid two-columns">
                            <Form.Item label="生效日期" name="effective_date" rules={[{ required: true, message: "请选择生效日期" }]}>
                                <DatePicker className="sales-revenue-full-width" format="YYYY-MM-DD" />
                            </Form.Item>
                            <Form.Item label="失效日期" name="expiry_date">
                                <DatePicker className="sales-revenue-full-width" format="YYYY-MM-DD" />
                            </Form.Item>
                            <Form.Item label="状态" name="status">
                                <Select options={ROUTE_STATUS_OPTIONS.filter((item) => item.value)} />
                            </Form.Item>
                        </div>
                    </div>
                </Form>
            </Modal>

            <Modal
                open={fuelTierModalOpen}
                title="编辑燃油阶梯"
                onCancel={() => setFuelTierModalOpen(false)}
                onOk={handleFuelTierSubmit}
                confirmLoading={fuelTierSubmitting}
                okText="保存"
                cancelText="取消"
                destroyOnClose
            >
                <Form form={fuelTierForm} layout="vertical">
                    <div className="sales-revenue-form-grid two-columns">
                        <Form.Item label="偏差下限 ($/MT)" name="diff_min" rules={[{ required: true, message: "请输入偏差下限" }]}>
                            <InputNumber className="sales-revenue-full-width" precision={2} />
                        </Form.Item>
                        <Form.Item label="偏差上限 ($/MT)" name="diff_max" rules={[{ required: true, message: "请输入偏差上限" }]}>
                            <InputNumber className="sales-revenue-full-width" precision={2} />
                        </Form.Item>
                        <Form.Item label="调整金额" name="adjustment_amount" rules={[{ required: true, message: "请输入调整金额" }]}>
                            <InputNumber className="sales-revenue-full-width" precision={2} />
                        </Form.Item>
                        <Form.Item label="币种" name="currency">
                            <Select options={currencyOptions} />
                        </Form.Item>
                        <Form.Item label="状态" name="status" className="sales-revenue-span-2">
                            <Select options={ROUTE_STATUS_OPTIONS.filter((item) => item.value)} />
                        </Form.Item>
                    </div>
                </Form>
            </Modal>

            <Modal
                open={fuelTierBatchOpen}
                title="批量添加燃油阶梯"
                onCancel={() => setFuelTierBatchOpen(false)}
                onOk={handleFuelTierBatchSubmit}
                confirmLoading={fuelTierBatchSubmitting}
                okText="批量保存"
                cancelText="取消"
                width={960}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    className="sales-revenue-modal-alert"
                    message="先用「等距生成」铺好多行，再在下方表格里逐格微调。偏差区间为闭区间，相邻区间不可相接（生成器已自动留 0.01 间隔）。空行会被忽略，不会保存。"
                />
                <div className="sales-revenue-ladder-bar">
                    <Form form={ladderForm} layout="inline" className="sales-revenue-ladder-form">
                        <Form.Item label="起点" name="start" rules={[{ required: true, message: "起点" }]}>
                            <InputNumber precision={2} style={{ width: 96 }} />
                        </Form.Item>
                        <Form.Item label="步长" name="step" rules={[{ required: true, message: "步长" }]}>
                            <InputNumber min={0.01} precision={2} style={{ width: 96 }} />
                        </Form.Item>
                        <Form.Item label="档数" name="count" rules={[{ required: true, message: "档数" }]}>
                            <InputNumber min={1} max={50} precision={0} style={{ width: 80 }} />
                        </Form.Item>
                        <Form.Item label="起始金额" name="amount_start" rules={[{ required: true, message: "起始金额" }]}>
                            <InputNumber precision={2} style={{ width: 96 }} />
                        </Form.Item>
                        <Form.Item label="每档递增" name="amount_step" rules={[{ required: true, message: "每档递增" }]}>
                            <InputNumber precision={2} style={{ width: 96 }} />
                        </Form.Item>
                        <Form.Item>
                            <Button icon={<ThunderboltOutlined />} onClick={generateLadderRows}>
                                等距生成（覆盖表格）
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
                <div className="sales-revenue-ladder-toolbar">
                    <Space size={8}>
                        <Button size="small" icon={<PlusOutlined />} onClick={() => addFuelTierRows(1)}>
                            加 1 行
                        </Button>
                        <Button size="small" icon={<PlusOutlined />} onClick={() => addFuelTierRows(5)}>
                            加 5 行
                        </Button>
                    </Space>
                    <span className="sales-revenue-card-meta">当前 {fuelTierRows.length} 行</span>
                </div>
                <Table
                    rowKey="key"
                    size="small"
                    pagination={false}
                    columns={fuelTierBatchColumns}
                    dataSource={fuelTierRows}
                    scroll={{ y: 320 }}
                    locale={{ emptyText: "暂无行，请点「加 1 行」或「等距生成」" }}
                />
            </Modal>

            <Modal
                open={prepaymentModalOpen}
                title="录入预付款"
                onCancel={() => setPrepaymentModalOpen(false)}
                onOk={handleCreatePrepayment}
                confirmLoading={prepaymentSubmitting}
                okText="提交"
                cancelText="取消"
                destroyOnClose
            >
                <Form form={prepaymentForm} layout="vertical">
                    <div className="sales-revenue-form-grid two-columns">
                        <Form.Item label="客户">
                            <Input value={detailContract?.customer_name || ""} disabled />
                        </Form.Item>
                        <Form.Item label="合同编号">
                            <Input value={detailContract?.contract_no || ""} disabled />
                        </Form.Item>
                        <Form.Item label="收款金额" name="amount" rules={[{ required: true, message: "请输入预付款金额" }]}>
                            <InputNumber className="sales-revenue-full-width" min={0.01} precision={2} />
                        </Form.Item>
                        <Form.Item label="币种" name="currency">
                            <Select options={currencyOptions} />
                        </Form.Item>
                        <Form.Item label="收款日期" name="received_date" rules={[{ required: true, message: "请选择收款日期" }]} className="sales-revenue-span-2">
                            <DatePicker className="sales-revenue-full-width" format="YYYY-MM-DD" />
                        </Form.Item>
                        <Form.Item label="备注" name="remark" className="sales-revenue-span-2">
                            <Input.TextArea rows={4} placeholder="如：签约预付、客户补款等" />
                        </Form.Item>
                    </div>
                </Form>
            </Modal>

            <Modal
                open={extraFeeModalOpen}
                title={extraFeeContext ? `新增额外费用：${extraFeeContext.booking_no || extraFeeContext.receivable_no}` : "新增额外费用"}
                onCancel={() => {
                    setExtraFeeModalOpen(false);
                    setExtraFeeContext(null);
                }}
                onOk={handleCreateExtraFeeReceivable}
                confirmLoading={extraFeeSubmitting}
                okText="新增应收"
                cancelText="取消"
                width={760}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    className="sales-revenue-modal-alert"
                    message="额外费用会作为独立待开票应收写入航班维度，并保留手工录入原因和事件日志。"
                />
                <Form form={extraFeeForm} layout="vertical">
                    <div className="sales-revenue-form-grid two-columns">
                        <Form.Item label="从当前页选择 CB 订单" name="booking_id">
                            <Select
                                allowClear
                                showSearch
                                options={extraFeeBookingOptions}
                                placeholder="可选；选择后自动回填订单号"
                                optionFilterProp="label"
                                onChange={(_, option) => {
                                    const item = option?.item;
                                    if (item) {
                                        extraFeeForm.setFieldsValue({
                                            booking_no: item.booking_no || "",
                                            contract_id: item.contract_id || null,
                                            currency: item.currency || "USD",
                                            billing_date: item.flight_date_snapshot ? dayjs(item.flight_date_snapshot) : dayjs(),
                                            billing_timezone: item.billing_timezone || "UTC",
                                        });
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item label="CB 订单号" name="booking_no" rules={[{ required: true, message: "请选择或输入 CB 订单号" }]}>
                            <Input placeholder="如 CB-202604-0002" />
                        </Form.Item>
                        <Form.Item label="归属合同" name="contract_id">
                            <Select
                                allowClear
                                showSearch
                                loading={receivableContractOptionsLoading}
                                options={receivableContractSelectOptions}
                                placeholder="可选；默认仅归属客户和订单"
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item label="费用类型" name="extra_fee_type" rules={[{ required: true, message: "请选择费用类型" }]}>
                            <Select options={EXTRA_FEE_TYPE_OPTIONS} />
                        </Form.Item>
                        <Form.Item label="金额" name="amount" rules={[{ required: true, message: "请输入金额" }]}>
                            <InputNumber className="sales-revenue-full-width" min={0.01} precision={2} />
                        </Form.Item>
                        <Form.Item label="币种" name="currency">
                            <Select options={currencyOptions} />
                        </Form.Item>
                        <Form.Item label="计费日期" name="billing_date" rules={[{ required: true, message: "请选择计费日期" }]}>
                            <DatePicker className="sales-revenue-full-width" format="YYYY-MM-DD" />
                        </Form.Item>
                        <Form.Item label="计费时区" name="billing_timezone">
                            <Input placeholder="默认 UTC；如 Asia/Hong_Kong" />
                        </Form.Item>
                        <Form.Item label="录入原因" name="manual_adjust_reason" className="sales-revenue-span-2" rules={[{ required: true, message: "请输入录入原因" }]}>
                            <Input.TextArea rows={4} placeholder="如：航班除冰费、额外地面操作费、客户确认补收费等" />
                        </Form.Item>
                    </div>
                </Form>
            </Modal>

            <Drawer
                open={bookingDetailOpen}
                title={bookingDetail ? `订单详情：${bookingDetail.booking_no}` : "订单详情"}
                onClose={() => setBookingDetailOpen(false)}
                width="min(1080px, 94vw)"
                destroyOnClose
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                        <Button onClick={() => setBookingDetailOpen(false)}>关闭</Button>
                        {canWriteReceivable && bookingDetailRecord ? (
                            <Button icon={<SyncOutlined />} onClick={() => handleReceivableRecalculate(bookingDetailRecord)}>
                                联动重算
                            </Button>
                        ) : null}
                        {canWriteReceivable && bookingDetailRecord ? (
                            <Button icon={<PlusOutlined />} onClick={() => openExtraFeeModal(bookingDetailRecord)}>
                                新增额外费用
                            </Button>
                        ) : null}
                    </div>
                }
            >
                {bookingDetail ? (
                    <>
                        {bookingDetail.summary?.has_exception ? (
                            <Alert
                                type="error"
                                showIcon
                                className="sales-revenue-alert"
                                message="该订单存在异常应收，请在下方明细中处理对应条目。"
                            />
                        ) : null}

                        <div className="sales-revenue-receivable-summary">
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">应收合计</div>
                                <div className="sales-revenue-receivable-summary-value sales-revenue-money">
                                    {formatMoney(bookingDetail.summary?.total_amount)}
                                    <span className="sales-revenue-receivable-summary-currency">{bookingDetail.summary?.currency}</span>
                                </div>
                            </div>
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">已收金额</div>
                                <div className="sales-revenue-receivable-summary-value sales-revenue-money">
                                    {formatMoney(bookingDetail.summary?.paid_amount)}
                                    <span className="sales-revenue-receivable-summary-currency">{bookingDetail.summary?.currency}</span>
                                </div>
                            </div>
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">未收金额</div>
                                <div
                                    className={`sales-revenue-receivable-summary-value sales-revenue-money ${
                                        Number(bookingDetail.summary?.unpaid_amount || 0) > 0
                                            ? "sales-revenue-money-warning"
                                            : "sales-revenue-money-success"
                                    }`}
                                >
                                    {formatMoney(bookingDetail.summary?.unpaid_amount)}
                                    <span className="sales-revenue-receivable-summary-currency">{bookingDetail.summary?.currency}</span>
                                </div>
                            </div>
                        </div>

                        <Descriptions
                            className="sales-revenue-desc"
                            bordered
                            size="small"
                            column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
                            labelStyle={{ width: 110, color: "var(--sales-text-secondary)", whiteSpace: "nowrap" }}
                            style={{ marginBottom: 16 }}
                            items={[
                                { key: "booking_no", label: "CB 订单", children: bookingDetail.booking_no || "—" },
                                {
                                    key: "status",
                                    label: "汇总状态",
                                    children: (
                                        <SectionTag tone={getToneClass("receivable-status", bookingDetail.summary?.status)}>
                                            {RECEIVABLE_STATUS_TEXT[bookingDetail.summary?.status] || bookingDetail.summary?.status}
                                        </SectionTag>
                                    ),
                                },
                                { key: "customer", label: "客户", children: bookingDetail.customer_name || "—" },
                                { key: "contract", label: "合同", children: bookingDetail.contract_no || "未归属合同" },
                                { key: "route", label: "航线", children: bookingDetail.route_display || "—" },
                                { key: "aircraft", label: "机型", children: bookingDetail.aircraft_type || "不限机型" },
                                {
                                    key: "std",
                                    label: "首段计划起飞",
                                    children: `${formatUtcDate(sortFlightLegs(bookingDetail.flight_legs_snapshot)[0]?.std || bookingDetail.flight_date)} · UTC`,
                                },
                                {
                                    key: "count",
                                    label: "应收条数",
                                    children: (() => {
                                        const list = bookingDetail.receivables || [];
                                        const cancelled = list.filter((item) => item.status === "CANCELLED").length;
                                        return `${list.length} 条${cancelled > 0 ? `（含 ${cancelled} 条已取消）` : ""}`;
                                    })(),
                                },
                            ]}
                        />

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>应收明细</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="_rowKey"
                                    size="small"
                                    pagination={false}
                                    columns={bookingReceivableDetailColumns}
                                    dataSource={buildBookingDetailRows(bookingDetail)}
                                    scroll={{ x: 970 }}
                                    locale={{ emptyText: "暂无应收明细" }}
                                />
                            </div>
                        </div>

                        {bookingDetail.contract_kind === "PER_LEG_BURN" ? (
                            <LegFuelBurnPanel
                                bookingId={bookingDetail.booking_id}
                                canWrite={canWriteContract}
                                canGenerate={canGenerateFuel}
                                messageApi={messageApi}
                                modalApi={modalApi}
                                onGenerated={loadReceivables}
                            />
                        ) : null}

                        {bookingDetail.contract_kind === "AD_HOC" ? (
                            <AdHocAmountPanel
                                bookingId={bookingDetail.booking_id}
                                canWrite={canWriteReceivable}
                                currencyOptions={currencyOptions}
                                messageApi={messageApi}
                                modalApi={modalApi}
                                onGenerated={loadReceivables}
                            />
                        ) : null}

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>航班动态</span>
                                <span className="sales-revenue-card-meta">实时取自航班运营（FOP），含已取消 / 被替换航段</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="flight_id"
                                    size="small"
                                    pagination={false}
                                    loading={bookingFlightsLoading}
                                    columns={bookingFlightColumns}
                                    dataSource={bookingFlights}
                                    scroll={{ x: 840 }}
                                    locale={{ emptyText: "暂无关联航班" }}
                                />
                            </div>
                        </div>
                    </>
                ) : null}
            </Drawer>

            <Drawer
                open={receivableDetailOpen}
                title={receivableDetail ? `应收详情：${receivableDetail.receivable_no}` : "应收详情"}
                onClose={() => setReceivableDetailOpen(false)}
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                        <Button onClick={() => setReceivableDetailOpen(false)}>关闭</Button>
                        {canWriteReceivable ? (
                            <Button icon={<SyncOutlined />} onClick={() => handleReceivableRecalculate(receivableDetail)}>
                                联动重算
                            </Button>
                        ) : null}
                        {canWriteReceivable ? (
                            <Button icon={<PlusOutlined />} onClick={() => openExtraFeeModal(receivableDetail)}>
                                新增额外费用
                            </Button>
                        ) : null}
                        {canWriteReceivable && receivableDetail?.linked_to_transport_booking ? (
                            <Tooltip title="该订单已挂在订舱上，请到订舱取消挂接或换挂">
                                <Button icon={<SwapOutlined />} disabled>
                                    改绑合同
                                </Button>
                            </Tooltip>
                        ) : null}
                        {canWriteReceivable && isReceivableRebindable(receivableDetail) ? (
                            <Button icon={<SwapOutlined />} onClick={() => openRebindContractModal(receivableDetail)}>
                                改绑合同
                            </Button>
                        ) : null}
                        {canWriteReceivable && !receivableDetail?.is_locked && receivableDetail?.status !== "CANCELLED" ? (
                            <Button icon={<EditOutlined />} onClick={() => openAdjustAmountModal(receivableDetail)}>
                                调整金额
                            </Button>
                        ) : null}
                        {canWriteReceivable && receivableDetail?.type === "BASE_FARE" ? (
                            <Button type="primary" icon={<SyncOutlined />} onClick={() => handleReceivableRematch(receivableDetail)}>
                                重新匹配
                            </Button>
                        ) : null}
                    </div>
                }
                width="min(880px, 92vw)"
                destroyOnClose
            >
                {receivableDetailLoading || !receivableDetail ? (
                    <div className="sales-revenue-loading">
                        <Spin />
                    </div>
                ) : (
                    <>
                        {receivableDetail.flight_pattern_drift ? (
                            <Alert
                                type="warning"
                                showIcon
                                className="sales-revenue-alert"
                                message={receivableDetail.flight_pattern_drift.message}
                                description={
                                    receivableDetail.flight_pattern_drift.can_recalculate
                                        ? "金额还没改。确认后按现在的飞法重算。"
                                        : "已经开票、收款或锁定，金额保持原样。"
                                }
                                action={
                                    canWriteReceivable && receivableDetail.flight_pattern_drift.can_recalculate ? (
                                        <Button size="small" onClick={() => handleReceivableRematch(receivableDetail)}>
                                            按现在的飞法重算
                                        </Button>
                                    ) : null
                                }
                            />
                        ) : null}

                        {receivableDetail.exception_code ? (
                            <Alert
                                type="error"
                                showIcon
                                className="sales-revenue-alert"
                                message={`${RECEIVABLE_EXCEPTION_TEXT[receivableDetail.exception_code] || receivableDetail.exception_code} · ${receivableDetail.exception_message || "请检查合同和航线价格配置"}`}
                            />
                        ) : null}

                        {!receivableDetail.exception_code && receivableDetail.warning_code ? (
                            <Alert
                                type="warning"
                                showIcon
                                className="sales-revenue-alert"
                                message={`${RECEIVABLE_WARNING_TEXT[receivableDetail.warning_code] || receivableDetail.warning_code} · ${receivableDetail.warning_message || "金额已正常生成，可正常开票"}`}
                            />
                        ) : null}

                        <div className="sales-revenue-receivable-summary">
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">应收金额</div>
                                <div className="sales-revenue-receivable-summary-value sales-revenue-money">
                                    {formatMoney(receivableDetail.amount)}
                                    <span className="sales-revenue-receivable-summary-currency">{receivableDetail.currency}</span>
                                </div>
                            </div>
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">已收金额</div>
                                <div className="sales-revenue-receivable-summary-value sales-revenue-money">
                                    {formatMoney(receivableDetail.paid_amount)}
                                    <span className="sales-revenue-receivable-summary-currency">{receivableDetail.currency}</span>
                                </div>
                            </div>
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">未收金额</div>
                                <div
                                    className={`sales-revenue-receivable-summary-value sales-revenue-money ${
                                        Number(getReceivableProgress(receivableDetail).remainingAmount) > 0
                                            ? "sales-revenue-money-warning"
                                            : "sales-revenue-money-success"
                                    }`}
                                >
                                    {formatMoney(getReceivableProgress(receivableDetail).remainingAmount)}
                                    <span className="sales-revenue-receivable-summary-currency">{receivableDetail.currency}</span>
                                </div>
                            </div>
                        </div>

                        <Descriptions
                            className="sales-revenue-desc"
                            bordered
                            size="small"
                            column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
                            labelStyle={{ width: 110, color: "var(--sales-text-secondary)", whiteSpace: "nowrap" }}
                            style={{ marginBottom: 16 }}
                            items={[
                                { key: "receivable_no", label: "应收编号", children: receivableDetail.receivable_no },
                                { key: "booking_no", label: "CB 订单", children: receivableDetail.booking_no || "—" },
                                {
                                    key: "status",
                                    label: "状态",
                                    children: (
                                        <SectionTag tone={getToneClass("receivable-status", receivableDetail.status)}>
                                            {RECEIVABLE_STATUS_TEXT[receivableDetail.status] || receivableDetail.status}
                                        </SectionTag>
                                    ),
                                },
                                { key: "customer", label: "客户", children: receivableDetail.customer_name || "—" },
                                { key: "contract", label: "合同", children: receivableDetail.contract_no || "—" },
                                { key: "type", label: "类型", children: RECEIVABLE_TYPE_TEXT[receivableDetail.type] || receivableDetail.type },
                                { key: "flight_date", label: "计费日期", children: formatDate(receivableDetail.flight_date_snapshot) },
                                { key: "route", label: "航线", children: receivableDetail.route_display_snapshot || "—" },
                                { key: "updated", label: "更新时间", children: formatDateTime(receivableDetail.updated_at) },
                            ]}
                        />

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>计费快照</span>
                            </div>
                            <div className="sales-revenue-card-body">
                                <Descriptions
                                    className="sales-revenue-desc"
                                    bordered
                                    size="small"
                                    column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
                                    labelStyle={{ width: 110, color: "var(--sales-text-secondary)", whiteSpace: "nowrap" }}
                                    items={[
                                        { key: "src", label: "计费日期来源", children: receivableDetail.billing_date_source || "—" },
                                        { key: "tz", label: "时区", children: receivableDetail.billing_timezone || "—" },
                                        { key: "aircraft", label: "机型", children: receivableDetail.aircraft_type_snapshot || "不限机型" },
                                        {
                                            key: "contract_match",
                                            label: "合同匹配",
                                            children:
                                                receivableDetail.pricing_snapshot?.contract_no || receivableDetail.contract_no || "—",
                                        },
                                        {
                                            key: "price_match",
                                            label: "价格匹配",
                                            children:
                                                PRICE_MATCH_TEXT[receivableDetail.pricing_snapshot?.match_type] ||
                                                receivableDetail.pricing_snapshot?.match_type ||
                                                "—",
                                        },
                                        { key: "route_source", label: "航线来源", children: receivableDetail.pricing_snapshot?.route_source || "—" },
                                        {
                                            key: "flight_pattern",
                                            label: "飞法",
                                            children: receivableDetail.pricing_snapshot?.flight_pattern_label || "—",
                                        },
                                    ]}
                                />
                                {receivableDetail.pricing_snapshot?.candidate_contract_nos?.length ? (
                                    <div className="sales-revenue-inline-note">
                                        候选合同：{receivableDetail.pricing_snapshot.candidate_contract_nos.join(" / ")}
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>航段快照</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey={(record) => record.flight_id || `${record.flight_number}-${record.rotation_leg_index}`}
                                    size="small"
                                    pagination={false}
                                    columns={receivableFlightLegColumns}
                                    dataSource={receivableDetail.flight_legs_snapshot || []}
                                    locale={{ emptyText: "暂无航段快照" }}
                                />
                            </div>
                        </div>

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>事件流水</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    columns={receivableEventColumns}
                                    dataSource={receivableDetail.event_logs || []}
                                    locale={{ emptyText: "暂无事件流水" }}
                                />
                            </div>
                        </div>

                        {receivableDetail.pricing_snapshot?.manual_adjustment?.history?.length > 0 && (
                            <div className="sales-revenue-card compact">
                                <div className="sales-revenue-card-header">
                                    <span>手工调整历史</span>
                                    {receivableDetail.manual_adjust_reason && (
                                        <Tag color="purple" style={{ marginLeft: 8 }}>当前已调整</Tag>
                                    )}
                                </div>
                                <div className="sales-revenue-card-body sales-revenue-table-body">
                                    <Table
                                        rowKey={(_, idx) => `adj-${idx}`}
                                        size="small"
                                        pagination={false}
                                        dataSource={receivableDetail.pricing_snapshot.manual_adjustment.history}
                                        locale={{ emptyText: "暂无调整记录" }}
                                        columns={[
                                            {
                                                title: "时间",
                                                dataIndex: "adjusted_at",
                                                key: "adjusted_at",
                                                width: 170,
                                                render: (v) => (v ? formatDateTime(v) : "—"),
                                            },
                                            {
                                                title: "金额变更",
                                                key: "amount_change",
                                                width: 200,
                                                render: (_, r) =>
                                                    r.cleared_by_recalc
                                                        ? <span className="sales-revenue-cell-muted">由重算清除</span>
                                                        : `${formatMoney(r.from_amount)} → ${formatMoney(r.to_amount)}`,
                                            },
                                            {
                                                title: "原因 / 触发",
                                                key: "reason",
                                                render: (_, r) =>
                                                    r.cleared_by_recalc
                                                        ? `强制重算（${r.recalc_trigger || "—"}）`
                                                        : r.reason || "—",
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Drawer>

            <Modal
                open={invoiceModalOpen}
                title="批量开票"
                onCancel={() => setInvoiceModalOpen(false)}
                onOk={handleCreateInvoice}
                confirmLoading={invoiceSubmitting}
                okText="确认开票"
                cancelText="取消"
                width={900}
                destroyOnClose
            >
                <Form form={invoiceForm} layout="vertical">
                    <div className="sales-revenue-form-grid two-columns">
                        <Form.Item label="发票号" name="invoice_no" rules={[{ required: true, message: "请输入发票号" }]}>
                            <Input placeholder="如 INV-2026-W15-001" />
                        </Form.Item>
                        <Form.Item label="开票日期" name="invoice_date" rules={[{ required: true, message: "请选择开票日期" }]}>
                            <DatePicker className="sales-revenue-full-width" format="YYYY-MM-DD" />
                        </Form.Item>
                        <Form.Item label="发票类型" name="invoice_type" rules={[{ required: true, message: "请选择发票类型" }]}>
                            <Select options={INVOICE_CREATE_TYPE_OPTIONS} onChange={handleInvoiceTypeChange} />
                        </Form.Item>
                        <Form.Item label="开票金额">
                            <Input value={`${formatMoney(invoiceCandidateTotalAmount)} USD`} disabled />
                        </Form.Item>
                        <Form.Item label="备注" name="remark" className="sales-revenue-span-2">
                            <Input placeholder="可选，如：周账单、月度调整等" />
                        </Form.Item>
                    </div>
                </Form>
                <div className="sales-revenue-selection-summary">
                    <span>已选 {selectedInvoiceReceivableIds.length} 条应收</span>
                    <span className="sales-revenue-money">合计 {formatMoney(invoiceCandidateTotalAmount)} USD</span>
                </div>
                <Table
                    rowKey="id"
                    size="small"
                    columns={invoiceCandidateColumns}
                    dataSource={invoiceCandidates}
                    pagination={false}
                    scroll={{ y: 320 }}
                    rowSelection={{
                        selectedRowKeys: selectedInvoiceReceivableIds,
                        onChange: (selectedRowKeys) => setSelectedInvoiceReceivableIds(selectedRowKeys),
                    }}
                    locale={{ emptyText: "当前筛选范围内没有可开票应收" }}
                />
            </Modal>

            <Modal
                open={invoiceDetailOpen}
                title={invoiceDetail ? `发票详情：${invoiceDetail.invoice_no}` : "发票详情"}
                onCancel={() => setInvoiceDetailOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setInvoiceDetailOpen(false)}>
                        关闭
                    </Button>,
                    canWritePayment && invoiceDetail && invoiceDetail.status !== "VOIDED" && getOutstandingAmount(invoiceDetail.total_amount, invoiceDetail.paid_amount) > 0 ? (
                        <Button key="payment" type="primary" onClick={() => openPaymentModal(invoiceDetail)}>
                            录入收款
                        </Button>
                    ) : null,
                    canWriteInvoice && invoiceDetail && invoiceDetail.status !== "VOIDED" && Number(invoiceDetail.paid_amount || 0) <= 0 ? (
                        <Button key="void" danger onClick={() => handleVoidInvoice(invoiceDetail)}>
                            作废发票
                        </Button>
                    ) : null,
                ]}
                width={1040}
                destroyOnClose
            >
                {invoiceDetailLoading || !invoiceDetail ? (
                    <div className="sales-revenue-loading">
                        <Spin />
                    </div>
                ) : (
                    <>
                        <div className="sales-revenue-desc-grid">
                            <div className="sales-revenue-desc-item">
                                <div className="sales-revenue-desc-label">发票号</div>
                                <div className="sales-revenue-desc-value">{invoiceDetail.invoice_no}</div>
                            </div>
                            <div className="sales-revenue-desc-item">
                                <div className="sales-revenue-desc-label">客户</div>
                                <div className="sales-revenue-desc-value">{invoiceDetail.customer_name || "—"}</div>
                            </div>
                            <div className="sales-revenue-desc-item">
                                <div className="sales-revenue-desc-label">合同</div>
                                <div className="sales-revenue-desc-value">{invoiceDetail.contract_no || "跨合同 / 未归属主合同"}</div>
                            </div>
                            <div className="sales-revenue-desc-item">
                                <div className="sales-revenue-desc-label">状态</div>
                                <div className="sales-revenue-desc-value">
                                    <SectionTag tone={getToneClass("invoice-status", invoiceDetail.status)}>
                                        {INVOICE_STATUS_TEXT[invoiceDetail.status] || invoiceDetail.status}
                                    </SectionTag>
                                </div>
                            </div>
                            <div className="sales-revenue-desc-item">
                                <div className="sales-revenue-desc-label">发票类型</div>
                                <div className="sales-revenue-desc-value">{INVOICE_TYPE_TEXT[invoiceDetail.invoice_type] || invoiceDetail.invoice_type}</div>
                            </div>
                            <div className="sales-revenue-desc-item">
                                <div className="sales-revenue-desc-label">开票日期</div>
                                <div className="sales-revenue-desc-value">{formatDate(invoiceDetail.invoice_date)}</div>
                            </div>
                        </div>

                        <div className="sales-revenue-receivable-summary">
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">发票金额</div>
                                <div className="sales-revenue-receivable-summary-value sales-revenue-money">
                                    {formatMoney(invoiceDetail.total_amount)} {invoiceDetail.currency}
                                </div>
                            </div>
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">已收金额</div>
                                <div className="sales-revenue-receivable-summary-value sales-revenue-money">
                                    {formatMoney(invoiceDetail.paid_amount)} {invoiceDetail.currency}
                                </div>
                            </div>
                            <div className="sales-revenue-receivable-summary-item">
                                <div className="sales-revenue-receivable-summary-label">待收金额</div>
                                <div className="sales-revenue-receivable-summary-value sales-revenue-money sales-revenue-money-success">
                                    {formatMoney(getOutstandingAmount(invoiceDetail.total_amount, invoiceDetail.paid_amount))} {invoiceDetail.currency}
                                </div>
                            </div>
                        </div>

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>关联应收</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="receivable_id"
                                    size="small"
                                    pagination={false}
                                    columns={invoiceReceivableColumns}
                                    dataSource={invoiceDetail.receivables || []}
                                    locale={{ emptyText: "暂无关联应收" }}
                                />
                            </div>
                        </div>

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>收款记录</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    columns={invoicePaymentColumns}
                                    dataSource={invoiceDetail.payments || []}
                                    locale={{ emptyText: "暂无收款记录" }}
                                />
                            </div>
                        </div>

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>收款分摊流水</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    columns={paymentAllocationColumns}
                                    dataSource={invoiceDetail.payment_allocations || []}
                                    locale={{ emptyText: "暂无分摊流水" }}
                                />
                            </div>
                        </div>
                    </>
                )}
            </Modal>

            <Modal
                open={paymentModalOpen}
                title="录入收款"
                onCancel={() => setPaymentModalOpen(false)}
                onOk={handleCreatePayment}
                confirmLoading={paymentSubmitting}
                okText="确认录入"
                cancelText="取消"
                width={760}
                destroyOnClose
            >
                <Form form={paymentForm} layout="vertical">
                    <div className="sales-revenue-form-grid two-columns">
                        <Form.Item label="关联发票">
                            <Input value={paymentInvoice?.invoice_no || ""} disabled />
                        </Form.Item>
                        <Form.Item label={`发票金额 (${paymentInvoice?.currency || "USD"})`}>
                            <Input value={paymentInvoice ? formatMoney(paymentInvoice.total_amount) : ""} disabled />
                        </Form.Item>
                        <Form.Item label={`收款金额 (${paymentInvoice?.currency || "USD"})`} name="amount" rules={[{ required: true, message: "请输入收款金额" }]}>
                            <InputNumber className="sales-revenue-full-width" min={0.01} precision={2} />
                        </Form.Item>
                        <Form.Item label="收款日期" name="payment_date" rules={[{ required: true, message: "请选择收款日期" }]}>
                            <DatePicker className="sales-revenue-full-width" format="YYYY-MM-DD" />
                        </Form.Item>
                        <Form.Item label="收款来源" name="source_type" rules={[{ required: true, message: "请选择收款来源" }]}>
                            <Select options={PAYMENT_SOURCE_OPTIONS} />
                        </Form.Item>
                        <Form.Item label="收款方式" name="payment_method" rules={[{ required: true, message: "请选择收款方式" }]}>
                            <Select
                                options={watchedPaymentSourceType === "CREDIT_LEDGER" ? CREDIT_PAYMENT_METHOD_OPTIONS : CUSTOMER_PAYMENT_METHOD_OPTIONS}
                            />
                        </Form.Item>
                        <Form.Item label="备注" name="remark" className="sales-revenue-span-2">
                            <Input placeholder="可选" />
                        </Form.Item>
                    </div>

                    {watchedPaymentSourceType === "CREDIT_LEDGER" ? (
                        <>
                            <Alert
                                type="info"
                                showIcon
                                className="sales-revenue-modal-alert"
                                message="可跨合同选择同一客户的可用余额。每笔抵扣会写入 credit usage 流水，并按发票内应收自动生成 payment allocation。"
                            />
                            <Form.List name="credit_usages">
                                {(fields, { add, remove }) => (
                                    <div className="sales-revenue-credit-usage-list">
                                        {fields.map((field) => (
                                            <div key={field.key} className="sales-revenue-credit-usage-row">
                                                <Form.Item
                                                    {...field}
                                                    label="余额来源"
                                                    name={[field.name, "credit_ledger_id"]}
                                                    className="sales-revenue-credit-usage-source"
                                                    rules={[{ required: true, message: "请选择余额来源" }]}
                                                >
                                                    <Select
                                                        showSearch
                                                        loading={customerCreditsLoading}
                                                        options={creditOptions}
                                                        placeholder="请选择余额来源"
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    {...field}
                                                    label="抵扣金额"
                                                    name={[field.name, "amount"]}
                                                    className="sales-revenue-credit-usage-amount"
                                                    rules={[{ required: true, message: "请输入抵扣金额" }]}
                                                >
                                                    <InputNumber className="sales-revenue-full-width" min={0.01} precision={2} />
                                                </Form.Item>
                                                <Button danger type="link" onClick={() => remove(field.name)}>
                                                    删除
                                                </Button>
                                            </div>
                                        ))}
                                        <Button
                                            type="dashed"
                                            onClick={() => add({ credit_ledger_id: undefined, amount: undefined })}
                                            disabled={customerCreditsLoading || !customerCredits.length}
                                        >
                                            添加余额来源
                                        </Button>
                                    </div>
                                )}
                            </Form.List>
                        </>
                    ) : null}
                </Form>
            </Modal>

            <Modal
                open={fuelCalcModalOpen}
                title="燃油调整计算（半自动）"
                onCancel={() => setFuelCalcModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setFuelCalcModalOpen(false)}>
                        关闭
                    </Button>,
                ]}
                width={1100}
                destroyOnClose
            >
                <Tabs
                    defaultActiveKey="TIER"
                    items={[
                        {
                            key: "TIER",
                            label: "阶梯合同",
                            children: (
                                <>
                <Alert
                    type="info"
                    showIcon
                    className="sales-revenue-modal-alert"
                    message="流程：① 选择目标月份和合同；② 系统基于参考月份 IATA 油价均值和合同阶梯表计算预览；③ 销售确认后批量生成燃油调整应收。"
                />
                <Form form={fuelCalcForm} layout="vertical">
                    <div className="sales-revenue-form-grid two-columns">
                        <Form.Item label="目标月份" name="target_month" rules={[{ required: true, message: "请选择目标月份" }]}>
                            <DatePicker picker="month" className="sales-revenue-full-width" />
                        </Form.Item>
                        <Form.Item label="合同" name="contract_id" rules={[{ required: true, message: "请选择合同" }]}>
                            <Select
                                showSearch
                                loading={receivableContractOptionsLoading}
                                options={tierContractSelectOptions}
                                placeholder="请选择合同（仅阶梯合同）"
                            />
                        </Form.Item>
                    </div>
                    <div className="sales-revenue-subsection-header">
                        <span>先计算预览，再确认生成</span>
                        <Button type="primary" loading={fuelCalcPreviewLoading} onClick={handleFuelCalcPreview}>
                            计算预览
                        </Button>
                    </div>
                </Form>

                {fuelCalcResult ? (
                    <div className="sales-revenue-batch-preview">
                        {!fuelCalcResult.is_ready ? (
                            <Alert
                                type="warning"
                                showIcon
                                className="sales-revenue-modal-alert"
                                message={fuelCalcResult.message || "参考月份 IATA 油价未全部确认，当前不能生成燃油调整应收。"}
                                description={
                                    fuelCalcResult.missing_week_end_dates?.length
                                        ? `缺失周：${fuelCalcResult.missing_week_end_dates.map((item) => formatDate(item)).join("、")}`
                                        : undefined
                                }
                            />
                        ) : fuelCalcResult.is_first_contract_month ? (
                            <Alert
                                type="info"
                                showIcon
                                className="sales-revenue-modal-alert"
                                message={fuelCalcResult.message || "合同首月不生成燃油调整。"}
                            />
                        ) : (
                            <Alert
                                type={fuelCalcResult.summary.error_count ? "warning" : "success"}
                                showIcon
                                className="sales-revenue-modal-alert"
                                message={fuelCalcResult.message || "计算完成，请确认预览结果。"}
                            />
                        )}

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">计算参数</div>
                            <div className="sales-revenue-card-body">
                                <div className="sales-revenue-desc-grid">
                                    <div className="sales-revenue-desc-item">
                                        <div className="sales-revenue-desc-label">目标月份</div>
                                        <div className="sales-revenue-desc-value">{fuelCalcResult.target_month || "—"}</div>
                                    </div>
                                    <div className="sales-revenue-desc-item">
                                        <div className="sales-revenue-desc-label">参考数据月份</div>
                                        <div className="sales-revenue-desc-value">{fuelCalcResult.reference_month || "—"}</div>
                                    </div>
                                    <div className="sales-revenue-desc-item">
                                        <div className="sales-revenue-desc-label">合同基准油价</div>
                                        <div className="sales-revenue-desc-value">
                                            {fuelCalcResult.base_fuel_price_per_mt == null
                                                ? "—"
                                                : `${formatMoney(fuelCalcResult.base_fuel_price_per_mt)} $/MT`}
                                        </div>
                                    </div>
                                    <div className="sales-revenue-desc-item">
                                        <div className="sales-revenue-desc-label">上月 $/MT 均值</div>
                                        <div className="sales-revenue-desc-value">
                                            {fuelCalcResult.average_price_per_mt == null
                                                ? "—"
                                                : `${formatMoney(fuelCalcResult.average_price_per_mt)} $/MT`}
                                        </div>
                                    </div>
                                    <div className="sales-revenue-desc-item">
                                        <div className="sales-revenue-desc-label">偏差值 (Avg - Benchmark)</div>
                                        <div className="sales-revenue-desc-value">
                                            {fuelCalcResult.diff == null ? "—" : formatMoney(fuelCalcResult.diff)}
                                        </div>
                                    </div>
                                    <div className="sales-revenue-desc-item">
                                        <div className="sales-revenue-desc-label">匹配阶梯区间</div>
                                        <div className="sales-revenue-desc-value">
                                            {fuelCalcResult.matched_tier
                                                ? `${formatMoney(fuelCalcResult.matched_tier.diff_min)} ~ ${formatMoney(
                                                      fuelCalcResult.matched_tier.diff_max,
                                                  )} -> ${formatMoney(fuelCalcResult.matched_tier.adjustment_amount)} ${
                                                      fuelCalcResult.matched_tier.currency
                                                  }`
                                                : "—"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>受影响 CB 订单 ({fuelCalcResult.summary.candidate_count} 笔)</span>
                                <Space size={8}>
                                    <span className="sales-revenue-card-meta">
                                        可生成 {fuelCalcResult.summary.ready_count} / 已存在 {fuelCalcResult.summary.reused_count} / 异常{" "}
                                        {fuelCalcResult.summary.error_count}
                                    </span>
                                    {canGenerateFuel ? (
                                        <Button
                                            type="primary"
                                            loading={fuelCalcGenerating}
                                            onClick={handleFuelCalcGenerate}
                                            disabled={
                                                !fuelCalcResult.is_ready ||
                                                fuelCalcResult.is_first_contract_month ||
                                                !fuelCalcResult.summary.ready_count
                                            }
                                        >
                                            确认生成应收
                                        </Button>
                                    ) : null}
                                </Space>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="booking_id"
                                    size="small"
                                    columns={fuelCalcColumns}
                                    dataSource={fuelCalcResult.items || []}
                                    pagination={false}
                                    scroll={{ y: 320, x: 1280 }}
                                />
                            </div>
                        </div>

                        <div className="sales-revenue-card compact">
                            <div className="sales-revenue-card-header">
                                <span>合同燃油附加费阶梯表 ({fuelCalcResult.contract_no})</span>
                            </div>
                            <div className="sales-revenue-card-body sales-revenue-table-body">
                                <Table
                                    rowKey="id"
                                    size="small"
                                    columns={fuelCalcTierColumns}
                                    dataSource={fuelCalcResult.fuel_surcharge_tiers || []}
                                    pagination={false}
                                    rowClassName={(record) =>
                                        fuelCalcResult?.matched_tier?.id === record.id ? "sales-revenue-fuel-tier-row-active" : ""
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ) : null}
                                </>
                            ),
                        },
                        {
                            key: "PER_LEG_BURN",
                            label: "按段油耗",
                            children: (
                                <PerLegBurnBatchPanel
                                    customerOptions={customerOptions}
                                    canGenerate={canGenerateFuel}
                                    messageApi={messageApi}
                                    modalApi={modalApi}
                                    onGenerated={loadReceivables}
                                />
                            ),
                        },
                    ]}
                />
            </Modal>

            <Modal
                open={batchGenerateModalOpen}
                title="基础运价按月补生成"
                onCancel={() => setBatchGenerateModalOpen(false)}
                onOk={handleBatchGenerateSubmit}
                confirmLoading={batchGenerateSubmitting}
                okText={watchedBatchGenerateDryRun ? "执行预览" : "确认补生成"}
                cancelText="取消"
                width={1100}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    className="sales-revenue-modal-alert"
                    message="按服务月份，为订单上已写合同的 CB 去程生成基础运价应收（金额=合同航线单价，与燃油无关）。订单上还没写合同的会自动跳过；先勾选「仅预览」可只看结果不落库。"
                />
                <Form form={batchGenerateForm} layout="vertical">
                    <div className="sales-revenue-form-grid two-columns">
                        <Form.Item label="服务月份" name="period" rules={[{ required: true, message: "请选择服务月份" }]}>
                            <DatePicker picker="month" className="sales-revenue-full-width" />
                        </Form.Item>
                        <Form.Item label="限定合同" name="contract_id">
                            <Select
                                allowClear
                                showSearch
                                loading={receivableContractOptionsLoading}
                                options={receivableContractSelectOptions}
                                placeholder="默认按客户自动匹配"
                            />
                        </Form.Item>
                        <Form.Item name="dryRun" valuePropName="checked" className="sales-revenue-span-2">
                            <Checkbox>仅预览，不写入数据库</Checkbox>
                        </Form.Item>
                    </div>
                </Form>

                {batchGenerateResult ? (
                    <div className="sales-revenue-batch-preview">
                        <div className="sales-revenue-batch-summary">
                            <div className="sales-revenue-batch-summary-item">
                                <div className="sales-revenue-batch-summary-label">总订单数</div>
                                <div className="sales-revenue-batch-summary-value">{batchGenerateResult.summary.total_bookings}</div>
                            </div>
                            <div className="sales-revenue-batch-summary-item">
                                <div className="sales-revenue-batch-summary-label">新增/预览</div>
                                <div className="sales-revenue-batch-summary-value">{batchGenerateResult.summary.create_count}</div>
                            </div>
                            <div className="sales-revenue-batch-summary-item">
                                <div className="sales-revenue-batch-summary-label">复用</div>
                                <div className="sales-revenue-batch-summary-value">{batchGenerateResult.summary.reused_count}</div>
                            </div>
                            <div className="sales-revenue-batch-summary-item">
                                <div className="sales-revenue-batch-summary-label">异常</div>
                                <div className="sales-revenue-batch-summary-value">{batchGenerateResult.summary.exception_count}</div>
                            </div>
                            <div className="sales-revenue-batch-summary-item">
                                <div className="sales-revenue-batch-summary-label">成功（带提醒）</div>
                                <div className="sales-revenue-batch-summary-value">{batchGenerateResult.summary.warned_count ?? 0}</div>
                            </div>
                            <div className="sales-revenue-batch-summary-item">
                                <div className="sales-revenue-batch-summary-label">合同不匹配</div>
                                <div className="sales-revenue-batch-summary-value">{batchGenerateResult.summary.skipped_contract_mismatch_count}</div>
                            </div>
                            {(batchGenerateResult.summary.skipped_ad_hoc_count ?? 0) > 0 ? (
                                <div className="sales-revenue-batch-summary-item">
                                    <div className="sales-revenue-batch-summary-label">一班一议（手填）</div>
                                    <div className="sales-revenue-batch-summary-value">{batchGenerateResult.summary.skipped_ad_hoc_count}</div>
                                </div>
                            ) : null}
                        </div>
                        {(batchGenerateResult.summary.acmi_pending_flights || []).length ||
                        batchGenerateResult.summary.acmi_incomplete_count ||
                        batchGenerateResult.summary.acmi_pending_message ? (
                            <Alert
                                type="info"
                                showIcon
                                className="sales-revenue-modal-alert"
                                message={batchGenerateResult.summary.acmi_pending_message || "还有航班没并进挂着这份合同的订单"}
                                description={
                                    <span>
                                        {(batchGenerateResult.summary.acmi_pending_flights || [])
                                            .map((flight) => flight.flight_number || flight.booking_no || "一班")
                                            .join("、") || "名单里没有可改挂的航班"}
                                        {batchGenerateResult.summary.acmi_incomplete_count
                                            ? `。还有 ${batchGenerateResult.summary.acmi_incomplete_count} 班没有机号或计划起飞时刻，不能改挂`
                                            : ""}
                                    </span>
                                }
                            />
                        ) : null}
                        {batchGenerateResult.summary.skipped_missing_contract_count > 0 ? (
                            <Alert
                                type="warning"
                                showIcon
                                className="sales-revenue-modal-alert"
                                message={
                                    <span>
                                        本月还有 <strong>{batchGenerateResult.summary.skipped_missing_contract_count}</strong> 笔订单未写合同，未纳入本次生成。
                                    </span>
                                }
                            />
                        ) : null}
                        <Table
                            rowKey={(record) => `${record.booking_id}-${record.action}`}
                            size="small"
                            columns={batchGenerateColumns}
                            dataSource={(batchGenerateResult.items || []).filter((item) => item.action !== "SKIP_MISSING_CONTRACT")}
                            pagination={false}
                            scroll={{ x: 1230, y: 320 }}
                            locale={{ emptyText: <Empty description="本次没有可生成的应收结果" /> }}
                        />
                    </div>
                ) : null}
            </Modal>

            <Modal
                open={rebindModalOpen}
                title="改绑合同"
                onCancel={closeRebindModal}
                onOk={handleRebindContract}
                okText="改绑并重算"
                cancelText="取消"
                confirmLoading={rebindSubmitting}
                width={520}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    className="sales-revenue-modal-alert"
                    message="改绑后系统会按新合同重新计算这张基础运价应收；只能改绑到该订单客户名下的其他合同。已开票/收款的应收已锁定，不在此处出现。"
                />
                <div className="sales-revenue-rebind-meta">
                    <div>
                        订单：<strong>{rebindTarget?.booking_no || "—"}</strong>
                    </div>
                    <div>
                        当前合同：<strong>{rebindTarget?.contract_no || "—"}</strong>
                    </div>
                </div>
                <Form form={bookingRebindForm} layout="vertical">
                    <Form.Item label="改绑到合同" name="contract_id" rules={[{ required: true, message: "请选择要改绑到的合同" }]}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            loading={rebindContractOptionsLoading}
                            options={rebindContractSelectOptions}
                            placeholder="请选择该客户名下的合同"
                            notFoundContent="该客户暂无其他合同"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={adjustAmountModalOpen}
                title="调整应收金额"
                onCancel={() => { setAdjustAmountModalOpen(false); setAdjustAmountTarget(null); }}
                onOk={handleAdjustAmountSubmit}
                okText="确认调整"
                cancelText="取消"
                confirmLoading={adjustAmountSubmitting}
                width={520}
                destroyOnClose
            >
                {adjustAmountTarget && (
                    <Descriptions column={2} size="small" className="sales-revenue-modal-desc" bordered={false}>
                        <Descriptions.Item label="应收编号">{adjustAmountTarget.receivable_no}</Descriptions.Item>
                        <Descriptions.Item label="类型">{RECEIVABLE_TYPE_TEXT[adjustAmountTarget.type] || adjustAmountTarget.type}</Descriptions.Item>
                        <Descriptions.Item label="当前金额">
                            <strong>{formatMoney(adjustAmountTarget.amount)} {adjustAmountTarget.currency}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                            {RECEIVABLE_STATUS_TEXT[adjustAmountTarget.status] || adjustAmountTarget.status}
                        </Descriptions.Item>
                    </Descriptions>
                )}
                <Form form={adjustAmountForm} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        label="新金额"
                        name="amount"
                        rules={[{ required: true, message: "请输入调整后金额" }]}
                    >
                        <InputNumber
                            style={{ width: "100%" }}
                            precision={2}
                            placeholder="请输入调整后金额（允许负数）"
                            addonAfter={adjustAmountTarget?.currency || "USD"}
                        />
                    </Form.Item>
                    <Form.Item
                        label="调整原因"
                        name="manual_adjust_reason"
                        rules={[
                            { required: true, message: "请输入调整原因" },
                            { min: 1, max: 500, message: "调整原因长度 1–500 字符" },
                        ]}
                    >
                        <Input.TextArea rows={3} maxLength={500} showCount placeholder="请说明调整原因" />
                    </Form.Item>
                </Form>
            </Modal>

            <Tour
                open={contractTourOpen}
                steps={contractTourSteps}
                onClose={() => setContractTourOpen(false)}
                onFinish={() => {
                    setContractTourOpen(false);
                    if (canWriteContract) {
                        openCreateContractModal();
                        setContractModalAutoGuide(true);
                    }
                }}
            />

            <Tour
                open={receivableTourOpen}
                steps={receivableTourSteps}
                onClose={() => setReceivableTourOpen(false)}
                onFinish={() => setReceivableTourOpen(false)}
            />
        </div>
    );
};

export default SalesRevenueManagementPage;
